import type { Translations } from './ru'

export const enTranslations: Translations = {
  langSwitcher: {
    aria: 'Switch language',
  },

  nav: {
    onlinePaint: 'Draw',
    pngJpg: 'PNG/JPG',
    cropImage: 'Crop',
    rotateImage: 'Rotate',
    blurImage: 'Blur',
    watermarkImage: 'Watermark',
    photo3x4: '3x4 photo',
    photoCollage: 'Collage',
    imageGifMaker: 'Photo GIF',
    compressImage: 'Compress',
    removeExif: 'EXIF',
    metadataViewer: 'Metadata',
    physicalPhotoSize: 'Print size',
    imageWebp: 'To WebP',
    webpConverter: 'WebP/JPG',
    avifConverter: 'AVIF',
    tiffJpg: 'TIFF/JPG',
    svgConverter: 'SVG',
    htmlToImage: 'HTML',
    resizeImage: 'Resize',
    socialMediaResize: 'Social sizes',
    upscaleImage: 'Upscale',
    faviconGenerator: 'Favicon',
    heicViewer: 'Open HEIC',
    jpgViewer: 'Open JPG',
    pngViewer: 'Open PNG',
    webpViewer: 'Open WebP',
    svgViewer: 'Open SVG',
    avifViewer: 'Open AVIF',
    imageColorPicker: 'Image color',
    tiffViewer: 'Open TIFF',
    gifViewer: 'Open GIF',
    bmpViewer: 'Open BMP',
    icoViewer: 'Open ICO',
    heicJpg: 'HEIC/JPG',
    blog: 'Blog',
  },

  badge: {
    local: 'Local',
    server: 'Server',
    localFirst: 'Local*',
    localTitle: 'The file is processed in your browser and is not sent to a server',
    serverTitle: 'The file is sent to a server for processing',
    localFirstTitle:
      'Processed in the browser by default. You can optionally send it to a server for stronger compression.',
  },

  common: {
    formats: 'PNG, JPG, JPEG',
    maxFileSize: 'Up to 50 MB',
    maxFileSizePerFile: 'Up to 50 MB per file',
    open: 'Open',
    convert: 'Convert',
    converting: 'Converting...',
    download: 'Download',
    done: 'Done',
    remove: 'Remove',
    errorConverting: 'Conversion error',
    batch: 'Batch',
    imageTools: 'Image tools',
    faq: 'Questions',
    updated: 'Updated',
    allBlogPosts: 'All blog posts',
  },

  index: {
    title: 'Image tools',
    description:
      'Open, convert or edit an image right in your browser. No sign-up, no watermarks.',
    searchPlaceholder: 'Find a tool: png, compress, crop',
    searchEmpty: 'Nothing found. Try another word, such as a file format.',
    groups: [
      { key: 'open', title: 'Open a file' },
      { key: 'convert', title: 'Convert' },
      { key: 'size', title: 'Compress and resize' },
      { key: 'edit', title: 'Edit' },
      { key: 'create', title: 'Create' },
      { key: 'data', title: 'Colour and metadata' },
    ],
    tools: [
      {
        group: 'open',
        title: 'Open PNG',
        description: 'With transparency on a checkerboard.',
        href: '/otkryt-png',
        formats: ['PNG', 'view'],
      },
      {
        group: 'open',
        title: 'Open SVG',
        description: 'Safe viewing and saving as PNG or JPG.',
        href: '/otkryt-svg',
        formats: ['SVG', 'vector', 'view'],
      },
      {
        group: 'open',
        title: 'Open HEIC',
        description: 'iPhone photos in the browser, JPG or PNG copy.',
        href: '/otkryt-heic',
        formats: ['HEIC', 'HEIF', 'iPhone', 'view'],
      },
      {
        group: 'open',
        title: 'Open JPG',
        description: 'Preview, file size, dimensions and next steps.',
        href: '/otkryt-jpg',
        formats: ['JPG', 'JPEG', 'view'],
      },
      {
        group: 'open',
        title: 'Open WebP',
        description: 'Preview and saving a frame as JPG or PNG.',
        href: '/otkryt-webp',
        formats: ['WebP', 'view'],
      },
      {
        group: 'open',
        title: 'Open AVIF',
        description: 'Preview and saving as JPG or PNG.',
        href: '/otkryt-avif',
        formats: ['AVIF', 'view'],
      },
      {
        group: 'open',
        title: 'Open TIFF',
        description: 'First frame and saving as JPG.',
        href: '/otkryt-tiff',
        formats: ['TIFF', 'TIF', 'view'],
      },
      {
        group: 'open',
        title: 'Open GIF',
        description: 'Animation in the browser, first frame as PNG.',
        href: '/otkryt-gif',
        formats: ['GIF', 'animation', 'view'],
      },
      {
        group: 'open',
        title: 'Open BMP',
        description: 'Preview and a PNG or JPG copy.',
        href: '/otkryt-bmp',
        formats: ['BMP', 'DIB', 'view'],
      },
      {
        group: 'open',
        title: 'Open ICO',
        description: 'Icon, stored sizes and a PNG copy.',
        href: '/otkryt-ico',
        formats: ['ICO', 'icon', 'favicon', 'view'],
      },
      {
        group: 'convert',
        title: 'PNG and JPG converter',
        description: 'PNG to JPG and JPG to PNG, several files at once.',
        href: '/konverter-izobrazheniy',
        formats: ['PNG', 'JPG', 'JPEG'],
      },
      {
        group: 'convert',
        title: 'HEIC to JPG, PNG or WebP',
        description: 'Up to 10 files with a format choice.',
        href: '/heic-v-jpg',
        formats: ['HEIC', 'HEIF', 'JPG', 'PNG', 'WebP', 'iPhone'],
      },
      {
        group: 'convert',
        title: 'JPG and PNG to WebP',
        description: 'Light WebP for sites, ZIP for several files.',
        href: '/v-webp',
        formats: ['JPG', 'PNG', 'WebP'],
      },
      {
        group: 'convert',
        title: 'WebP to JPG or PNG',
        description: 'A familiar format with a quality setting.',
        href: '/webp-v-jpg-png',
        formats: ['WebP', 'JPG', 'PNG'],
      },
      {
        group: 'convert',
        title: 'AVIF converter',
        description: 'JPG, PNG and WebP to AVIF and back.',
        href: '/avif-konverter',
        formats: ['AVIF', 'JPG', 'PNG', 'WebP'],
      },
      {
        group: 'convert',
        title: 'TIFF to JPG',
        description: 'Several files and a quality setting.',
        href: '/tiff-v-jpg',
        formats: ['TIFF', 'TIF', 'JPG'],
      },
      {
        group: 'convert',
        title: 'SVG to PNG or JPG',
        description: 'A raster copy at the size you need.',
        href: '/svg-v-png-jpg',
        formats: ['SVG', 'PNG', 'JPG'],
      },
      {
        group: 'convert',
        title: 'HTML to image',
        description: 'Paste code and download PNG or JPG.',
        href: '/html-v-kartinku',
        formats: ['HTML', 'PNG', 'JPG', 'screenshot'],
      },
      {
        group: 'size',
        title: 'Compress image',
        description: 'Reduce JPG, PNG or WebP size, also to a target size.',
        href: '/szhat-izobrazhenie',
        formats: ['JPG', 'PNG', 'WebP', 'compress'],
      },
      {
        group: 'size',
        title: 'Resize photo',
        description: 'New width and height, 50%, 25% or a custom size.',
        href: '/izmenit-razmer-foto',
        formats: ['JPG', 'PNG', 'WebP', 'resize'],
      },
      {
        group: 'size',
        title: 'Photo size for social media',
        description: 'Up to 24 crops for six formats, one by one or in ZIP.',
        href: '/razmer-foto-dlya-socsetey',
        formats: ['JPG', 'ZIP', 'social'],
      },
      {
        group: 'size',
        title: 'Upscale photo',
        description: '2x, 3x, 4x or a custom size.',
        href: '/uvelichit-foto',
        formats: ['JPG', 'PNG', 'WebP', 'upscale'],
      },
      {
        group: 'size',
        title: '3x4 photo',
        description: 'A document crop: 300x400, 600x800 or custom.',
        href: '/foto-3x4',
        formats: ['JPG', 'PNG', 'WebP', '3x4', 'passport'],
      },
      {
        group: 'size',
        title: 'Photo size in centimetres',
        description: 'Find the print size and PPI.',
        href: '/razmer-foto-v-sm',
        formats: ['JPG', 'PNG', 'WebP', 'DPI', 'PPI', 'print'],
      },
      {
        group: 'edit',
        title: 'Crop photo',
        description: 'Free selection and popular ratios.',
        href: '/obrezat-foto',
        formats: ['JPG', 'PNG', 'WebP', 'crop'],
      },
      {
        group: 'edit',
        title: 'Rotate photo',
        description: 'By 90, 180 or 270 degrees, with mirroring.',
        href: '/povernut-foto',
        formats: ['JPG', 'PNG', 'WebP', 'mirror'],
      },
      {
        group: 'edit',
        title: 'Blur photo',
        description: 'Hide a face or text with blur or pixelation.',
        href: '/razmyt-foto',
        formats: ['JPG', 'PNG', 'WebP', 'pixelate', 'hide'],
      },
      {
        group: 'edit',
        title: 'Watermark photo',
        description: 'Text over JPG, PNG or WebP.',
        href: '/vodyanoj-znak-na-foto',
        formats: ['JPG', 'PNG', 'WebP', 'watermark'],
      },
      {
        group: 'create',
        title: 'Draw online',
        description: 'Canvas, brush, shapes, arrows and text.',
        href: '/risovat-online',
        formats: ['PNG', 'JPG', 'WebP', 'paint'],
      },
      {
        group: 'create',
        title: 'Photo collage',
        description: '2 to 12 photos in a ready grid.',
        href: '/kollazh-iz-foto',
        formats: ['JPG', 'PNG', 'WebP', 'collage'],
      },
      {
        group: 'create',
        title: 'Make a GIF from photos',
        description: '2 to 20 frames with delays and loops.',
        href: '/sozdat-gif-iz-foto',
        formats: ['JPG', 'PNG', 'WebP', 'GIF', 'animation'],
      },
      {
        group: 'create',
        title: 'Favicon generator',
        description: 'A ZIP with a set of site icons.',
        href: '/favicon-generator',
        formats: ['PNG', 'JPG', 'WebP', 'ZIP', 'ICO', 'icon'],
      },
      {
        group: 'data',
        title: 'Pick a colour from an image',
        description: 'The exact pixel colour in HEX, RGB, HSL or HSV.',
        href: '/vybrat-cvet-s-kartinki',
        formats: ['JPG', 'PNG', 'WebP', 'HEX', 'eyedropper'],
      },
      {
        group: 'data',
        title: 'View EXIF and metadata',
        description: 'Camera, date and location in JPG, TIFF or HEIC.',
        href: '/posmotret-exif',
        formats: ['JPG', 'TIFF', 'HEIC', 'EXIF', 'location'],
      },
      {
        group: 'data',
        title: 'Remove EXIF',
        description: 'Clean metadata from JPG before publishing.',
        href: '/udalit-exif',
        formats: ['JPG', 'JPEG', 'EXIF', 'location'],
      },
    ],
  },

  rotateImage: {
    eyebrow: 'Rotate and flip',
    title: 'Rotate photo online',
    description:
      'Upload a JPG, PNG or WebP image, rotate it by 90/180/270 degrees or flip it. The result is saved locally in your browser with no server upload.',
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, rotate the photo or flip it horizontally and vertically.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Rotation',
    before: 'Before',
    after: 'After',
    waitingPreview: 'After rotation',
    notReady: 'Choose an action',
    download: 'Download result',
    summary: 'Summary',
    originalSize: 'Source file',
    originalDimensions: 'Original size',
    resultSize: 'After processing',
    rotationLabel: 'Rotation',
    flipLabel: 'Flip',
    flipHorizontalShort: 'horiz.',
    flipVerticalShort: 'vert.',
    none: 'none',
    privacyNote:
      'The file is not sent to a server. The image is opened and saved through canvas directly in your browser.',
    controls: [
      { action: 'rotate-left', label: 'Left 90°' },
      { action: 'rotate-right', label: 'Right 90°' },
      { action: 'rotate-180', label: 'Rotate 180°' },
      { action: 'flip-horizontal', label: 'Flip horizontal' },
      { action: 'flip-vertical', label: 'Flip vertical' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Press rotate left, rotate right, 180 degrees or flip on the needed axis.',
      'Check the result preview and download the finished image.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for rotation.',
      unsupportedOutput:
        'This browser cannot save the result in the source WebP format through canvas.',
      transformFailed: 'Could not transform the image.',
      noImage: 'Choose an image first.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. The image is opened, transformed and saved through canvas directly in your browser. The file stays on your device.',
      },
      {
        question: 'Which actions are available?',
        answer:
          'You can rotate the photo left or right by 90 degrees, rotate it by 180 degrees, and flip it horizontally or vertically.',
      },
      {
        question: 'Which format is used for the download?',
        answer:
          'The page tries to keep the source format: JPG stays JPG, PNG stays PNG and WebP stays WebP when the browser supports that export.',
      },
    ],
  },

  svgViewer: {
    howToTitle: 'How to use',
    eyebrow: 'Safe SVG viewer',
    title: 'Open SVG online',
    description:
      'Open and view an SVG online without server upload. The file is parsed as XML and active content or external resources are blocked before preview.',
    acceptedFormats: 'SVG',
    dropTitle: 'Drop an SVG here',
    dropDescription:
      'Choose one SVG up to 10 MB.',
    chooseFile: 'Choose SVG',
    loading: 'Checking SVG...',
    previewTitle: 'Sanitized SVG preview',
    previewAlt: 'Safe preview of a sanitized SVG',
    dimensions: 'Dimensions',
    dimensionsMissing: 'Not specified in SVG',
    exportDescription:
      'Viewing is the primary action. If needed, save the sanitized SVG as PNG or JPG with a white background.',
    exportPng: 'Download PNG',
    exportJpg: 'Download JPG',
    exporting: 'Preparing...',
    exportMissingDimensions:
      'Export requires width and height or a valid viewBox. You can still view this SVG.',
    exportFailed: 'Could not export this SVG. Try another file.',
    relatedToolsTitle: 'Next SVG steps',
    privacyNote:
      'The file is not sent to a server. Preview uses only a sanitized SVG copy; source markup is never inserted into the page or executed.',
    relatedTools: [
      {
        title: 'SVG to PNG or JPG',
        description: 'Choose result size, output format and JPG background color.',
        href: '/svg-v-png-jpg',
      },
      {
        title: 'Favicon generator',
        description: 'Prepare a favicon set from an image for a website.',
        href: '/favicon-generator',
      },
      {
        title: 'Resize image',
        description: 'Set a new width and height for a raster copy.',
        href: '/izmenit-razmer-foto',
      },
    ],
    howTo: [
      'Choose one SVG file on your device or drop it into the upload area.',
      'Wait for XML validation and active-content sanitization, then inspect the safe preview.',
      'Check the name, file size and detected dimensions; optionally download a PNG or JPG copy.',
    ],
    errorsText: {
      unsupported: 'Only SVG files are supported.',
      too_large: 'The SVG is too large. Maximum size is 10 MB.',
      too_large_image: 'The SVG declares image dimensions that are too large.',
      corrupt: 'Could not read this SVG. Check its XML markup and file structure.',
      decode_failure: 'The browser could not display the sanitized SVG. It may use unsupported constructs.',
    },
    processingErrors: {
      unsupported: 'Only SVG files are supported.',
      tooLarge: 'The SVG is too large. Maximum size is 10 MB.',
      invalidSvg: 'The file contains corrupt XML or is not a valid SVG.',
      unsafeSvg: 'The SVG contains active or unsafe content and cannot be displayed.',
      externalResource:
        'The SVG references an external resource. Safe local viewing requires a self-contained SVG.',
      unsupportedSvg:
        'The browser could not render the sanitized SVG. The file may use unsupported constructs.',
      canvas: 'The SVG dimensions are too large for canvas export.',
      conversion: 'Could not export the SVG to the selected format.',
    },
    faq: [
      {
        question: 'Is the SVG sent to a server?',
        answer:
          'No. Reading, validation, sanitization, preview and export all run locally in your browser. The file stays on your device.',
      },
      {
        question: 'Is it safe to open an SVG in a browser?',
        answer:
          'Scripts, event handlers, foreignObject, external links and dangerous CSS constructs are blocked before preview. Only the serialized sanitized copy is displayed.',
      },
      {
        question: 'Why might an SVG fail to open?',
        answer:
          'The viewer rejects corrupt XML, active content, external resources and SVG constructs the browser cannot safely render as an image.',
      },
      {
        question: 'Can I save the SVG as PNG or JPG?',
        answer:
          'Yes, as an optional action after viewing. Export requires width and height or a valid viewBox; JPG uses a white background.',
      },
    ],
  },

  tiffViewer: {
    howToTitle: 'How to use',
    eyebrow: 'TIFF viewer',
    title: 'Open TIFF online',
    description: 'Open a TIFF or TIF online and view its first frame locally in your browser.',
    acceptedFormats: 'TIFF, TIF',
    dropTitle: 'Drop a TIFF or TIF here',
    dropDescription: 'Choose one TIFF or TIF file.',
    chooseFile: 'Choose TIFF',
    loading: 'Decoding TIFF...',
    previewTitle: 'First TIFF frame',
    previewAlt: 'Preview of the first TIFF frame',
    dimensions: 'First-frame dimensions',
    frames: 'Pages / frames',
    downloadJpg: 'Download first frame as JPG',
    exportingJpg: 'Preparing JPG...',
    exportError: 'Could not download the JPG. Try opening the TIFF again.',
    relatedToolsTitle: 'More TIFF actions',
    multiFrameWarning: 'This file contains multiple pages or frames. The viewer displays and exports the first frame only.',
    privacyNote: 'The file is not sent to a server. TIFF is decoded locally and the JPG preview is created through browser canvas.',
    relatedTools: [
      { title: 'TIFF to JPG', description: 'Convert multiple TIFF files.', href: '/tiff-v-jpg' },
      { title: 'Compress image', description: 'Reduce the exported JPG size.', href: '/szhat-izobrazhenie' },
      { title: 'Resize image', description: 'Set a new image width and height.', href: '/izmenit-razmer-foto' },
    ],
    howTo: ['Choose one TIFF or TIF file.', 'Inspect the first frame and page count.', 'Download the first frame as JPG if needed.'],
    errorsText: {
      unsupported: 'Only TIFF and TIF files are supported.', too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image: 'The TIFF frame exceeds safe browser processing limits.', corrupt: 'The TIFF file is corrupted or has the wrong format.',
      decode_failure: 'Could not open this TIFF. It may use unsupported compression or a format variant.',
    },
    faq: [
      { question: 'Is the TIFF sent to a server?', answer: 'No. Validation, decoding, preview and JPG export run locally in your browser.' },
      { question: 'What happens with a multi-page TIFF?', answer: 'The viewer reports the page count but displays and exports only the first frame.' },
      { question: 'Can I save the opened TIFF as JPG?', answer: 'Yes. Download the displayed first frame as JPG.' },
    ],
  },

  avifViewer: {
    howToTitle: 'How to use', eyebrow: 'AVIF viewer', title: 'Open AVIF online', description: 'Open AVIF directly in your browser, inspect file dimensions and export JPG or PNG without server upload.', acceptedFormats: 'AVIF', dropTitle: 'Drop an AVIF here', dropDescription: 'Choose one AVIF up to 50 MB.', chooseFile: 'Choose AVIF', loading: 'Opening...', previewTitle: 'AVIF preview', previewAlt: 'AVIF preview', dimensions: 'Dimensions', relatedToolsTitle: 'Next steps', privacyNote: 'The file is not sent to a server.', exportDescription: 'PNG keeps transparency; JPG fills transparent areas with white.', downloadJpg: 'Download JPG', downloadPng: 'Download PNG', exportingJpg: 'Preparing JPG...', exportingPng: 'Preparing PNG...', exportError: 'Could not export the image.', exportLimit: 'JPG and PNG export is available for images up to 16 megapixels.', relatedTools: [{ title: 'AVIF converter', description: 'Convert AVIF.', href: '/avif-konverter' }, { title: 'Compress image', description: 'Reduce image size.', href: '/szhat-izobrazhenie' }, { title: 'Resize image', description: 'Set new dimensions.', href: '/izmenit-razmer-foto' }], howTo: ['Choose one .avif file.', 'Wait for local decoding and inspect dimensions.', 'Optionally download JPG or PNG.'], errorsText: { unsupported: 'Only AVIF images are supported.', too_large: 'The file is too large. Maximum size is 50 MB.', too_large_image: 'The image exceeds 36 megapixels.', corrupt: 'Could not open this AVIF. The file is corrupted or unsupported.', decode_failure: 'This browser cannot open AVIF images.' }, faq: [{ question: 'Is the AVIF sent to a server?', answer: 'No. All operations run locally in your browser.' }, { question: 'Why does an AVIF fail to open?', answer: 'The file may be corrupted or use an unsupported AVIF variant.' }, { question: 'Can I save AVIF as JPG or PNG?', answer: 'Yes, after a successful preview.' }],
  },

  imageColorPicker: {
    eyebrow: 'Image eyedropper',
    title: 'Pick a color from an image online',
    description:
      'Upload a JPG, PNG or WebP, zoom in and click the pixel you need. The file stays in your browser and the color can be copied as HEX, RGB, HSL or HSV.',
    dropTitle: 'Drop an image or choose a file',
    dropHint: 'You can also paste an image from the clipboard. Remote URLs are not supported, and the file is never uploaded.',
    loading: 'Opening image…',
    fit: 'Reset view',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    canvasLabel: 'Image used to pick a color',
    canvasKeyboardHint:
      'Use arrow keys to navigate pixels, Enter or Space to select a color. Shift + arrow moves 10 pixels.',
    pixelAnnouncement: 'Pixel {x} horizontally, {y} vertically.',
    selectedAnnouncement: ' Selected color {color}.',
    canvasHint: 'Hover for the loupe, click or tap to lock a color. Drag the image to pan after zooming in. Also supports keyboard: focus the image, use arrow keys to navigate, Enter to select.',
    resultTitle: 'Selected color',
    noSelection: 'Click a pixel in the image',
    transparent: 'This pixel is fully transparent. Channel values are shown without blending against the checkerboard.',
    copied: 'Copied',
    reset: 'Open another image',
    faqTitle: 'Image color picker questions',
    relatedTitle: 'More image tools',
    relatedCrop: 'Crop photo',
    relatedResize: 'Resize image',
    relatedCompress: 'Compress image',
    howTo: [
      'Upload a JPG, PNG or WebP with the file picker, drag and drop, or clipboard paste.',
      'Zoom and pan the image, then place the loupe over the pixel you need.',
      'Click or tap the pixel and copy its HEX, RGB, HSL or HSV value.',
    ],
    errors: {
      unsupported: 'Only JPG, PNG and WebP are supported.',
      tooLarge: 'This file is larger than 50 MB. Choose a smaller image.',
      tooManyPixels: 'This image is larger than 16 megapixels. Resize it before picking a color.',
      corrupt: 'The image could not be recognized. It may be corrupt or its format may not match the extension.',
      canvas: 'The browser could not prepare the image for pixel reading.',
      copy: 'Could not copy the value. Select it manually.',
    },
    faq: [
      {
        question: 'Is the image uploaded to a server?',
        answer: 'No. Decoding and pixel reads happen locally with browser Canvas. The file contents are not sent to a backend.',
      },
      {
        question: 'Why does a transparent PNG use eight-digit HEX?',
        answer: 'The final two HEX8 digits store alpha. The tool does not blend transparent pixels with the background and also shows rgba and hsla.',
      },
      {
        question: 'Can I pick a color from a website URL?',
        answer: 'No. The MVP accepts only a local file or clipboard image to preserve privacy and avoid CORS failures.',
      },
      {
        question: 'Which color space is used?',
        answer: 'Browser Canvas returns pixels in sRGB. Source Display-P3 and ICC profiles are not separately preserved or inspected.',
      },
    ],
  },

  viewer: {
    nextStepsTitle: 'What to do with the file',
    downloadPng: 'Download PNG',
    downloadJpg: 'Download JPG',
    preparing: 'Preparing file...',
    exportError: 'Could not save the file. Open it again and retry.',
    compress: 'Compress',
    crop: 'Crop',
    resize: 'Resize',
    convert: 'Convert',
    saveCopyFirst: 'Compress, crop and resize work with JPG, PNG and WebP. Download a copy first.',
    openAnother: 'Open another file',
    cancel: 'Cancel',
    otherFormatsTitle: 'Open another format',
    factsTitle: 'File details',
    fileName: 'Name',
    fileSize: 'File size',
    mimeType: 'MIME',
    format: 'Format',
    zoomFit: 'Fit',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
  },

  gifViewer: {
    howToTitle: 'How to use',
    eyebrow: 'GIF viewer',
    title: 'Open GIF online',
    description: 'Open a GIF online: the animation plays right in your browser and the file stays on your device. Save the first frame as PNG or JPG.',
    acceptedFormats: 'GIF',
    dropTitle: 'Drop a GIF here',
    dropDescription: 'Choose one GIF up to 50 MB.',
    chooseFile: 'Choose GIF',
    loading: 'Opening...',
    previewTitle: 'GIF preview',
    previewAlt: 'Opened GIF file',
    dimensions: 'Dimensions',
    relatedToolsTitle: 'More GIF actions',
    privacyNote: 'The file is not sent to a server. Your browser checks and plays the GIF on your device.',
    exportNote: 'PNG and JPG keep the first frame. JPG replaces transparency with white.',
    relatedTools: [
      {
        title: 'Make a GIF from photos',
        description: 'Build an animation from several images.',
        href: '/sozdat-gif-iz-foto',
      },
      {
        title: 'Compress image',
        description: 'Reduce the saved frame size.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'Open WebP',
        description: 'View an animation or frame in WebP.',
        href: '/otkryt-webp',
      },
    ],
    howTo: [
      'Choose one GIF file on your device or drop it into the upload area.',
      'Watch the animation and check the name, file size and dimensions.',
      'Optionally download the first frame as PNG or JPG.',
    ],
    errorsText: {
      unsupported: 'Only GIF images are supported.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image: 'This GIF is too large for the browser. Try an image under 36 megapixels.',
      corrupt: 'No GIF header found. The file is corrupted or has the wrong extension.',
      decode_failure: 'The browser could not open this GIF. Try saving the file again.',
    },
    faq: [
      {
        question: 'Is the GIF sent to a server?',
        answer: 'No. The file opens in your browser and stays on your device.',
      },
      {
        question: 'Will the animation play?',
        answer: 'Yes. The browser plays an animated GIF as on any web page, with the delays and loops stored in the file.',
      },
      {
        question: 'Can I save a GIF as PNG or JPG?',
        answer: 'Yes, but only the first frame: PNG and JPG do not store animation. PNG keeps transparency; JPG replaces it with white.',
      },
    ],
  },

  bmpViewer: {
    howToTitle: 'How to use',
    eyebrow: 'BMP viewer',
    title: 'Open BMP online',
    description: 'Open a BMP online, check its dimensions and download a PNG or JPG copy. The file stays on your device.',
    acceptedFormats: 'BMP, DIB',
    dropTitle: 'Drop a BMP here',
    dropDescription: 'Choose one BMP up to 50 MB.',
    chooseFile: 'Choose BMP',
    loading: 'Opening...',
    previewTitle: 'BMP preview',
    previewAlt: 'Opened BMP file',
    dimensions: 'Dimensions',
    relatedToolsTitle: 'More BMP actions',
    privacyNote: 'The file is not sent to a server. Your browser checks and opens the BMP on your device.',
    relatedTools: [
      {
        title: 'Compress image',
        description: 'Reduce the size of a PNG or JPG copy.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'Resize image',
        description: 'Set a new width and height for the copy.',
        href: '/izmenit-razmer-foto',
      },
      {
        title: 'Open TIFF',
        description: 'View another uncompressed raster format.',
        href: '/otkryt-tiff',
      },
    ],
    howTo: [
      'Choose one BMP file on your device or drop it into the upload area.',
      'Inspect the image and check the name, file size and dimensions.',
      'Optionally download a PNG or JPG copy.',
    ],
    errorsText: {
      unsupported: 'Only BMP images are supported.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image: 'This BMP is too large for the browser. Try an image under 36 megapixels.',
      corrupt: 'No BMP header found. The file is corrupted or has the wrong extension.',
      decode_failure: 'The browser could not open this BMP. It may use a rare format variant.',
    },
    faq: [
      {
        question: 'Is the BMP sent to a server?',
        answer: 'No. The file opens in your browser and stays on your device.',
      },
      {
        question: 'Why are BMP files so large?',
        answer: 'BMP usually stores pixels without compression. A PNG copy keeps the same pixels in much less space.',
      },
      {
        question: 'How do I save a BMP as PNG or JPG?',
        answer: 'Open the file and press a download button. The copy is created in your browser at the same dimensions.',
      },
    ],
  },

  icoViewer: {
    howToTitle: 'How to use',
    eyebrow: 'ICO viewer',
    title: 'Open ICO online',
    description: 'Open an ICO online, view the icon and the sizes stored in the file, and download a PNG or JPG copy. The file stays on your device.',
    acceptedFormats: 'ICO',
    dropTitle: 'Drop an ICO here',
    dropDescription: 'Choose one ICO up to 10 MB.',
    chooseFile: 'Choose ICO',
    loading: 'Opening...',
    previewTitle: 'ICO preview',
    previewAlt: 'Opened ICO icon',
    dimensions: 'Displayed size',
    storedSizes: 'Sizes in the file',
    relatedToolsTitle: 'More ICO actions',
    privacyNote: 'The file is not sent to a server. Your browser checks and opens the ICO on your device.',
    exportNote: 'The browser shows one size from the file and that size goes into the copy. JPG replaces transparency with white.',
    relatedTools: [
      {
        title: 'Favicon generator',
        description: 'Build a set of site icons from an image.',
        href: '/favicon-generator',
      },
      {
        title: 'Open PNG',
        description: 'Check the transparency of the downloaded copy.',
        href: '/otkryt-png',
      },
      {
        title: 'Open SVG',
        description: 'View a vector icon.',
        href: '/otkryt-svg',
      },
    ],
    howTo: [
      'Choose one ICO file on your device or drop it into the upload area.',
      'View the icon on the checkerboard background and the list of stored sizes.',
      'Optionally download a PNG or JPG copy.',
    ],
    errorsText: {
      unsupported: 'Only ICO files are supported.',
      too_large: 'The file is too large. Maximum size is 10 MB.',
      too_large_image: 'This icon is too large for the browser.',
      corrupt: 'No ICO header found. The file is corrupted or has the wrong extension.',
      decode_failure: 'The browser could not open this ICO. Try another browser or save the icon again.',
    },
    faq: [
      {
        question: 'Is the ICO sent to a server?',
        answer: 'No. The file opens in your browser and stays on your device.',
      },
      {
        question: 'Why does an ICO contain several sizes?',
        answer: 'One ICO file can store an icon at different sizes, such as 16, 32 and 48 pixels. The page lists them.',
      },
      {
        question: 'Can I save an ICO as PNG?',
        answer: 'Yes. The PNG copy keeps transparency and uses the size the browser displayed.',
      },
    ],
  },

  pngViewer: {
    howToTitle: 'How to use',
    eyebrow: 'PNG viewer',
    title: 'Open PNG online',
    description:
      'Open a PNG online and inspect transparent areas on a checkerboard background. Name, file size, dimensions and format are read locally in your browser.',
    acceptedFormats: 'PNG',
    dropTitle: 'Drop a PNG here',
    dropDescription:
      'Choose one PNG up to 50 MB.',
    chooseFile: 'Choose PNG',
    loading: 'Opening...',
    previewTitle: 'PNG preview',
    previewAlt: 'PNG preview on a transparency background',
    dimensions: 'Dimensions',
    exportNote: 'JPG replaces transparency with white.',
    relatedToolsTitle: 'More PNG actions',
    privacyNote:
      'The file is not sent to a server. PNG is validated and opened through the browser image API; preview and facts stay on your device.',
    relatedTools: [
      {
        title: 'PNG -> JPG',
        description: 'Create a JPG copy with a background for services without transparency support.',
        href: '/konverter-izobrazheniy',
      },
      {
        title: 'JPG/PNG -> WebP',
        description: 'Prepare a more compact WebP copy for a website.',
        href: '/v-webp',
      },
      {
        title: 'Compress image',
        description: 'Re-encode the PNG locally and compare its new file size.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'Resize image',
        description: 'Set a new width and height while preserving transparency.',
        href: '/izmenit-razmer-foto',
      },
    ],
    howTo: [
      'Choose one PNG file on your device or drop it into the upload area.',
      'Inspect the image and transparent areas on the checkerboard background, then adjust zoom if needed.',
      'Check the name, file size, dimensions and format, then continue to conversion, compression or resizing.',
    ],
    errorsText: {
      unsupported: 'Only PNG images are supported.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image:
        'This PNG is too large to open in the browser. Try an image under 36 megapixels.',
      corrupt: 'The PNG signature is missing. The file is corrupted or has the wrong extension.',
      decode_failure:
        'The PNG signature is valid, but the browser could not decode the image. Try resaving the file.',
    },
    faq: [
      {
        question: 'How can I see PNG transparency?',
        answer:
          'Transparent and semi-transparent areas appear over a checkerboard, so they are distinct from white image pixels.',
      },
      {
        question: 'Is the PNG sent to a server?',
        answer:
          'No. Format validation, decoding, preview and file facts are handled locally in your browser.',
      },
      {
        question: 'What can I do after viewing it?',
        answer:
          'You can convert PNG to JPG or WebP, compress the image, or change its width and height.',
      },
    ],
  },

  webpViewer: {
    eyebrow: 'WebP viewer',
    title: 'Open WebP online',
    description:
      'Open a WebP online, inspect the image and check its name, file size, dimensions and format. Everything runs locally without sending the file to a server.',
    acceptedFormats: 'WebP',
    dropTitle: 'Drop a WebP here',
    dropDescription:
      'Choose one WebP up to 50 MB.',
    chooseFile: 'Choose WebP',
    loading: 'Opening...',
    previewTitle: 'WebP preview',
    previewAlt: 'WebP image preview',
    dimensions: 'Dimensions',
    relatedToolsTitle: 'More WebP actions',
    privacyNote:
      'The file is not sent to a server. WebP validation, decoding, preview and export run locally in your browser.',
    downloadJpg: 'Download JPG copy',
    downloadPng: 'Download PNG copy',
    exportingJpg: 'Preparing JPG...',
    exportingPng: 'Preparing PNG...',
    exportError:
      'Could not export the image. This browser may not support the selected canvas export or may be out of memory.',
    unsupportedBrowser:
      'This browser cannot decode WebP. Open the file in a modern browser; no server fallback is used.',
    staticFrameNote:
      'Animated WebP exports as one static frame. Animation and metadata are not preserved; JPG replaces transparency with a white background.',
    relatedTools: [
      {
        title: 'WebP to JPG or PNG',
        description: 'Open the dedicated converter and choose the output format.',
        href: '/webp-v-jpg-png',
      },
      {
        title: 'Compress image',
        description: 'Reduce WebP file size with local quality controls.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'Resize image',
        description: 'Set a new image width and height.',
        href: '/izmenit-razmer-foto',
      },
    ],
    howToTitle: 'How to use',
    howTo: [
      'Choose one WebP file on your device or drop it into the upload area.',
      'Inspect the preview, use zoom/fit controls and check the name, file size and dimensions.',
      'Optionally download a static JPG or PNG copy, or continue to conversion, compression or resizing.',
    ],
    errorsText: {
      unsupported: 'Only WebP images are supported.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image:
        'This WebP is too large to open in the browser. Try an image under 36 megapixels.',
      corrupt:
        'The WebP signature is missing. The file is corrupted or has the wrong extension.',
      decode_failure:
        'The WebP signature is valid, but the browser could not open the file. It may be corrupted or use an unsupported WebP variant.',
    },
    faq: [
      {
        question: 'How do I open a WebP online?',
        answer:
          'Choose a .webp file. Your browser validates it locally and shows the image together with its dimensions and file size.',
      },
      {
        question: 'Is the WebP sent to a server?',
        answer:
          'No. The file is decoded and displayed directly in your browser. There is no server fallback or file upload.',
      },
      {
        question: 'Can I save the WebP as JPG or PNG?',
        answer:
          'Yes. Optional canvas exports appear after the file opens. PNG preserves transparency while JPG uses a white background. Animation exports as one static frame.',
      },
    ],
  },

  blurImage: {
    eyebrow: 'Photo privacy',
    title: 'Blur photo online',
    description:
      'Upload a JPG, PNG or WebP image, manually select rectangular areas and cover them with blur or pixelation. Processing runs locally in your browser.',
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, draw rectangles over the areas you want to hide.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Areas',
    modeLabel: 'Effect',
    modes: [
      { key: 'blur', label: 'Blur' },
      { key: 'pixelate', label: 'Pixels' },
    ],
    strength: 'Effect strength',
    zones: 'Areas',
    noZones: 'Draw an area on the image',
    undoZone: 'Undo',
    clearZones: 'Clear',
    before: 'Before',
    after: 'After',
    waitingPreview: 'Result preview',
    notReady: 'Draw an area',
    previewUpdating: 'Updating preview...',
    download: 'Download result',
    exporting: 'Preparing file...',
    manualNote:
      'Areas are selected manually. Automatic face or license plate detection is not used in this MVP.',
    privacyNote:
      'The file is not sent to a server. Blur and pixelation run through canvas directly in your browser.',
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Draw one or more rectangles over the areas you want to hide.',
      'Choose blur or pixelation, adjust effect strength and download the result.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for processing.',
      unsupportedOutput:
        'This browser cannot save the result in the source WebP format through canvas.',
      exportFailed: 'Could not prepare the image.',
      noImage: 'Choose an image first.',
      noZones: 'Draw at least one area first.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. The image is opened, processed and saved through canvas directly in your browser. The file stays on your device.',
      },
      {
        question: 'Is there automatic face detection?',
        answer:
          'No. In this version, areas are selected manually with rectangles, so the user keeps full control over the result.',
      },
      {
        question: 'Should I choose blur or pixelation?',
        answer:
          'Blur hides details more softly, while pixelation makes the covered area more obvious. You can adjust the strength before downloading.',
      },
    ],
  },

  watermarkImage: {
    eyebrow: 'Text watermark',
    title: 'Watermark photo online',
    description:
      'Upload a JPG, PNG or WebP image, add a text watermark, adjust position, opacity, size and rotation. Processing runs locally in your browser.',
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, set the text, position, opacity, size and output format.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Watermark',
    textLabel: 'Text',
    positionLabel: 'Position',
    opacityLabel: 'Opacity',
    sizeLabel: 'Size',
    rotationLabel: 'Rotation',
    outputFormat: 'Output format',
    applyButton: 'Download with watermark',
    retryDownload: 'Retry download',
    processing: 'Preparing file...',
    sourceLabel: 'Source',
    resultLabel: 'Result',
    waitingPreview: 'Watermark preview',
    emptyPreview: 'Text',
    summary: 'Summary',
    originalSize: 'Source file',
    originalDimensions: 'Original size',
    privacyNote:
      'The file is not sent to a server. The image is opened and saved through canvas directly in your browser.',
    positions: [
      { key: 'top-left', label: 'Top left' },
      { key: 'top-center', label: 'Top' },
      { key: 'top-right', label: 'Top right' },
      { key: 'center', label: 'Center' },
      { key: 'bottom-left', label: 'Bottom left' },
      { key: 'bottom-center', label: 'Bottom' },
      { key: 'bottom-right', label: 'Bottom right' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Enter watermark text and choose position, opacity, size and rotation.',
      'Select JPG, PNG or WebP output and download the watermarked file in one action.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for the watermark.',
      unsupportedOutput:
        'This browser cannot save the result in the selected WebP format through canvas.',
      emptyText: 'Enter watermark text.',
      exportFailed: 'Could not prepare the watermarked image.',
    },
    faq: [
      {
        question: 'Is the photo sent to a server?',
        answer:
          'No. The image is opened, the watermark is drawn and the result is saved through canvas directly in your browser. The file stays on your device.',
      },
      {
        question: 'Can I customize watermark placement and appearance?',
        answer:
          'Yes. You can choose seven positions, opacity from 10% to 100%, text size and rotation from -45 to 45 degrees.',
      },
      {
        question: 'Which format can I download?',
        answer:
          'You can choose JPG, PNG or WebP. JPG is useful for photos without transparency, PNG preserves transparency and WebP depends on browser support.',
      },
    ],
    relatedTitle: 'Related tools',
    related: [
      {
        title: 'Blur photo',
        description: 'Cover image areas with blur or pixelation.',
        href: '/razmyt-foto',
      },
      {
        title: 'Resize photo',
        description: 'Prepare an image with the needed width and height before publishing.',
        href: '/izmenit-razmer-foto',
      },
      {
        title: 'Compress image',
        description: 'Reduce the finished file size after adding a watermark.',
        href: '/szhat-izobrazhenie',
      },
    ],
  },

  crop: {
    page: {
      eyebrow: 'Crop photo',
      title: 'Crop photo online',
      description:
        'Upload a JPG, PNG or WebP image, select an area and download the cropped picture. Processing runs locally in your browser with no file upload.',
      faq: [
        {
          question: 'Is the file sent to a server?',
          answer:
            'No. The image is opened and cropped in your browser via canvas. The file stays on your device.',
        },
        {
          question: 'Which aspect ratios are available?',
          answer:
            'You can use free crop, square 1:1, 4:3, 16:9 and vertical 3:4. Move the area or resize it by the corners.',
        },
        {
          question: 'Which format is used for the download?',
          answer:
            'The result keeps the source format: JPG stays JPG, PNG stays PNG and WebP stays WebP.',
        },
      ],
    },
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, select the crop area with a mouse or finger.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Crop',
    aspectRatio: 'Aspect ratio',
    width: 'Width',
    height: 'Height',
    sourceLabel: 'Source',
    resultLabel: 'Crop',
    waitingPreview: 'Crop preview',
    notReady: 'Preparing preview',
    previewUpdating: 'Updating preview...',
    download: 'Download crop',
    exporting: 'Preparing file...',
    resizeHandle: 'Resize crop area',
    privacyNote:
      'The file is not sent to a server. Cropping runs locally in your browser.',
    presets: [
      { key: 'free', label: 'Free', analyticsKey: 'free' },
      { key: 'square', label: '1:1', analyticsKey: '1_1' },
      { key: 'landscape', label: '4:3', analyticsKey: '4_3' },
      { key: 'wide', label: '16:9', analyticsKey: '16_9' },
      { key: 'portrait', label: '3:4', analyticsKey: '3_4' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Select the crop area and choose an aspect ratio when needed.',
      'Click the download button to save the cropped image.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for cropping.',
      cropFailed: 'Could not crop the image.',
      noImage: 'Choose an image first.',
    },
  },

  photo3x4: {
    page: {
      eyebrow: '3x4 photo',
      title: '3x4 photo online',
      description:
        'Upload a JPG, PNG or WebP image, position the fixed 3:4 frame and download a 300x400, 600x800 or custom-size picture. Everything runs locally in your browser.',
      faq: [
        {
          question: 'Is the file sent to a server?',
          answer:
            'No. The photo is opened, cropped and resized in your browser through canvas. The file stays on your device.',
        },
        {
          question: 'Is this an official passport or visa format?',
          answer:
            'No. The page creates an image with a 3:4 ratio and the selected pixel size, but it does not validate requirements for any authority.',
        },
        {
          question: 'Can I choose another size?',
          answer:
            'Yes. Use the 300x400 and 600x800 presets, or enter a custom size with width and height locked to 3:4.',
        },
      ],
    },
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop a photo here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, move the 3:4 frame and choose the output size.',
    chooseFile: 'Choose photo',
    replaceImage: 'Replace',
    settings: '3x4 photo',
    outputSize: 'Output size',
    width: 'Width',
    height: 'Height',
    aspectLock: 'The 3:4 ratio is locked for both the frame and the result.',
    backgroundColor: 'Transparency background',
    cropSize: 'Crop',
    targetSize: 'Result',
    previewTitle: 'Preview',
    sourceLabel: 'Source crop',
    resultLabel: '3x4 photo',
    waitingPreview: 'After processing',
    notReady: 'Not ready',
    previewUpdating: 'Updating preview...',
    processing: 'Preparing photo...',
    download: 'Download photo',
    retry: 'Retry preparation',
    resizeHandle: 'Resize 3x4 frame',
    noComplianceNote:
      'This is not an official compliance check. Before submitting a document, verify size, background and face requirements with the relevant authority.',
    privacyNote:
      'The file is not sent to a server. Cropping, background fill and resizing run locally in your browser.',
    presets: [
      { key: '300x400', label: '300x400' },
      { key: '600x800', label: '600x800' },
      { key: 'custom', label: 'Custom' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Move the 3:4 frame and choose a 300x400, 600x800 or custom output size.',
      'Wait for the current preview and download the finished image with one button.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      tooLargeOutput: 'The output size is too large for browser processing.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for the 3x4 photo.',
      unsupportedOutput:
        'This browser cannot save the result in the source WebP format through canvas.',
      exportFailed: 'Could not prepare the 3x4 photo.',
      noImage: 'Choose an image first.',
    },
  },

  resize: {
    page: {
      eyebrow: 'Resize',
      title: 'Resize photo online',
      description:
        'Upload a JPG, PNG or WebP image, enter a new width and height, and download the downsized picture. Everything runs locally in your browser.',
      faq: [
        {
          question: 'Is the file sent to a server?',
          answer:
            'No. The image is opened, resized and saved through canvas directly in your browser. The file stays on your device.',
        },
        {
          question: 'Can I keep the original aspect ratio?',
          answer:
            'Yes. Enable the aspect ratio lock: changing width recalculates height automatically, and changing height recalculates width.',
        },
        {
          question: 'Which format is used for the download?',
          answer:
            'The result keeps the source format when the browser supports that export: JPG stays JPG, PNG stays PNG and WebP stays WebP.',
        },
      ],
    },
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, enter a new size or pick a quick preset.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Size',
    presetsTitle: 'Preset',
    width: 'Width',
    height: 'Height',
    keepAspect: 'Keep aspect ratio',
    jpegQuality: 'JPG quality',
    webpQuality: 'WebP quality',
    previewUpdating: 'Updating preview...',
    processing: 'Preparing file...',
    download: 'Download result',
    retry: 'Retry preparation',
    before: 'Before',
    after: 'After',
    waitingPreview: 'Result preview',
    notReady: 'After processing',
    summary: 'Summary',
    originalSize: 'Source file',
    targetSize: 'New size',
    resultSize: 'After processing',
    privacyNote:
      'The file is not sent to a server. Resizing runs locally in your browser.',
    presets: [
      { key: '50', label: '50%' },
      { key: '25', label: '25%' },
      { key: 'custom', label: 'Custom' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Enter a new width and height, keep proportions or choose 50%/25%.',
      'Wait for the current preview and download the finished image with one button.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for resizing.',
      unsupportedOutput:
        'This browser cannot save the result in the source WebP format through canvas.',
      resizeFailed: 'Could not resize the image.',
      noImage: 'Choose an image first.',
    },
  },

  upscale: {
    page: {
      eyebrow: 'Image upscale',
      title: 'Upscale photo online',
      description:
        'Upload a JPG, PNG or WebP image and enlarge it in your browser. The tool resizes through canvas and does not promise AI detail recovery.',
      faq: [
        {
          question: 'Is this AI upscale?',
          answer:
            'No. The MVP uses local canvas scaling with high smoothing. It increases pixel dimensions, but it does not recover missing detail like AI models.',
        },
        {
          question: 'Is the file sent to a server?',
          answer:
            'No. The image is opened, scaled and saved through canvas directly in your browser. The file stays on your device.',
        },
        {
          question: 'When is this useful?',
          answer:
            'Use it when you need a larger pixel file for a layout, a small print, or a service that requires minimum dimensions. Strong quality improvement needs a separate AI/server workflow.',
        },
      ],
    },
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose a JPG, PNG or WebP file up to 50 MB. After upload, pick 2x, 3x, 4x or enter a custom size up to 4x.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Upscale',
    presetsTitle: 'Scale',
    width: 'Width',
    height: 'Height',
    keepAspect: 'Keep aspect ratio',
    jpegQuality: 'JPG quality',
    webpQuality: 'WebP quality',
    upscaleButton: 'Upscale',
    processing: 'Preparing file...',
    download: 'Download result',
    before: 'Before',
    after: 'After',
    waitingPreview: 'After upscale',
    notReady: 'After processing',
    qualityNotice:
      'Method: local browser canvas scaling. It does not add new details and is not AI super-resolution.',
    summary: 'Summary',
    originalSize: 'Source file',
    scaleLabel: 'Scale',
    targetSize: 'New size',
    resultSize: 'After processing',
    privacyNote:
      'The file is not sent to a server. Upscaling runs locally through browser image APIs and canvas.toBlob.',
    presets: [
      { key: '2', label: '2x' },
      { key: '3', label: '3x' },
      { key: '4', label: '4x' },
      { key: 'custom', label: 'Custom' },
    ],
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Pick 2x, 3x, 4x or enter width and height manually.',
      'Click upscale and download the larger image.',
    ],
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      tooLargeOutput:
        'The output size is too large for browser processing. Reduce the scale or size.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for upscaling.',
      unsupportedOutput:
        'This browser cannot save the result in the source WebP format through canvas.',
      upscaleFailed: 'Could not upscale the image.',
      noImage: 'Choose an image first.',
    },
  },

  faviconGenerator: {
    eyebrow: 'Favicon package',
    title: 'Favicon generator online',
    description:
      'Upload a PNG, JPG or WebP logo, choose contain or cover square fit, and download a ready favicon package for your website.',
    dropTitle: 'Drop a logo here',
    dropDescription:
      'Choose a PNG, JPG or WebP file up to 50 MB. The generator creates square PNG files at 16, 32, 180, 192 and 512 px.',
    chooseFile: 'Choose image',
    replaceImage: 'Replace',
    settings: 'Favicon',
    fitMode: 'Square',
    fitModes: [
      { key: 'contain', label: 'Contain' },
      { key: 'cover', label: 'Cover' },
    ],
    generateButton: 'Generate ZIP',
    processing: 'Preparing package...',
    downloadZip: 'Download ZIP',
    previewTitle: 'Crop preview',
    outputPreviewTitle: 'Favicon preview',
    packageTitle: 'Files in ZIP',
    packageFiles: [
      'favicon-16x16.png',
      'favicon-32x32.png',
      'apple-touch-icon.png',
      'icon-192.png',
      'icon-512.png',
    ],
    privacyNote:
      'The file is not sent to a server. The image is opened and resized with canvas in your browser, and the ZIP is built locally.',
    howTo: [
      'Choose a PNG, JPG or WebP image on your device.',
      'Pick the square mode: contain the whole image or cover the square from the center.',
      'Click generate and download a ZIP with favicon PNG files and site.webmanifest.',
    ],
    errorsText: {
      unsupported: 'Only PNG, JPG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for the favicon.',
      packageFailed: 'Could not build the favicon package.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. The image is opened, resized and packaged as a ZIP directly in your browser. The file stays on your device.',
      },
      {
        question: 'Which files are included?',
        answer:
          'The ZIP contains favicon-16x16.png, favicon-32x32.png, apple-touch-icon.png, icon-192.png, icon-512.png and site.webmanifest.',
      },
      {
        question: 'What is the difference between contain and cover?',
        answer:
          'Contain keeps the full image inside the square with transparent padding. Cover fills the whole square and may crop the edges.',
      },
    ],
  },

  converter: {
    page: {
      eyebrow: 'PNG ↔ JPG',
      title: 'PNG and JPG image converter',
      description:
        'Convert PNG to JPG or JPG to PNG for free. Choose a direction, control JPG quality and download results without sending files to a server.',
      howToTitle: 'How to convert PNG and JPG',
      howTo: [
        'Choose PNG or JPG/JPEG as the source format.',
        'Add images; their format is detected from the file contents.',
        'Set JPG quality when applicable and start the local conversion.',
        'Download each result or all converted images in a ZIP archive.',
      ],
      faq: [
        {
          question: 'Are files sent to a server?',
          answer:
            'No. PNG and JPG files are decoded and converted locally through canvas in your browser.',
        },
        {
          question: 'What happens to PNG transparency when converting to JPG?',
          answer:
            'JPG does not support transparency, so transparent areas are filled with a white background.',
        },
        {
          question: 'Will converting JPG to PNG improve image quality?',
          answer:
            'No. PNG avoids another lossy encoding pass but cannot restore details already lost to JPEG compression.',
        },
        {
          question: 'Can I convert several images at once?',
          answer:
            'Yes. Every file in one queue must match the selected source format.',
        },
      ],
    },
    dropTitle: 'Drop images here',
    dropDescription: 'Choose {format} files. Their contents are checked, and you can add several files at once.',
    inputFormat: 'Source format',
    chooseFiles: 'Choose files',
    clearAll: 'Clear list',
    filesTitle: 'Files',
    downloadAll: 'Download all',
    settings: 'Settings',
    outputFormat: 'Output format',
    recommended: 'Recommended:',
    jpegCompact: 'JPEG (more compact)',
    pngLossless: 'PNG (lossless)',
    jpegDefault: 'JPEG',
    jpegQuality: 'JPG quality',
    transparencyWarning: 'Transparent PNG areas are filled with white because JPG does not support transparency.',
    pngQualityNote: 'PNG has no quality control. Conversion cannot restore details already lost in the source JPG.',
    noFilesQueued: 'No files queued',
    summary: 'Summary',
    pending: 'Queued',
    done: 'Done',
    errors: 'Errors',
    originalSize: 'Original size',
    convertedSize: 'After conversion',
    privacyNote: 'Files are not sent to a server. Conversion runs in your browser.',
    errorsText: {
      canvas: 'Could not create a canvas',
      imageLoad: 'Could not load the image',
      conversion: 'Image conversion failed',
      generic: 'Conversion error',
      unsupported: 'Only genuine PNG, JPG and JPEG files are supported.',
      wrongDirection: 'The file does not match the selected source format.',
      tooLarge: 'The file exceeds the 50 MB limit.',
      invalidContainer: 'The file is corrupt or contains an incomplete image container.',
      tooManyPixels: 'The image exceeds 36 megapixels or 16,384 pixels on one side.',
      tooManyFiles: 'One queue can contain no more than 20 files.',
      aggregateInputTooLarge: 'The files in the queue exceed 200 MB in total.',
      aggregatePixelsTooLarge: 'The images in the queue exceed 144 megapixels in total.',
      outputZipWorkloadTooLarge: 'The queue is too large to prepare outputs and a ZIP archive safely.',
    },
  },

  imageToWebp: {
    eyebrow: 'JPG and PNG to WebP',
    title: 'JPG and PNG to WebP converter',
    description:
      'Convert JPG, JPEG and PNG to WebP online for free. Files are processed locally in your browser, with quality control and size comparison before download.',
    dropTitle: 'Drop JPG or PNG files here',
    dropDescription:
      'Choose JPG, JPEG or PNG files up to 50 MB. Add multiple files, preview the WebP result and download everything as a ZIP archive.',
    chooseFiles: 'Choose images',
    qualityLabel: 'WebP quality',
    sourceLabel: 'Source',
    resultLabel: 'WebP',
    privacyNote:
      'Files are not sent to a server. The image is opened through browser image APIs and saved as WebP with canvas.toBlob.',
    qualityHowTo: 'choose the WebP size and quality balance',
    errorsText: {
      unsupported: 'Only JPG, JPEG and PNG images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      unsupportedWebp:
        'This browser cannot save images as WebP through canvas. Try updating your browser or use another one.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for WebP.',
      conversion: 'Could not convert this image to WebP.',
      generic: 'WebP conversion error',
    },
    faq: [
      {
        question: 'Are files sent to a server?',
        answer:
          'No. JPG and PNG files are opened and encoded to WebP directly in your browser through canvas. Files stay on your device.',
      },
      {
        question: 'What does the quality slider do?',
        answer:
          'The value is passed to canvas.toBlob(image/webp). Lower quality usually means a smaller file and more visible detail loss.',
      },
      {
        question: 'Can I convert several files at once?',
        answer:
          'Yes. Add multiple JPG or PNG files, convert them to WebP and download each result separately or as one ZIP archive.',
      },
    ],
  },

  webpConverter: {
    eyebrow: 'WebP to JPG and PNG',
    title: 'WebP to JPG and PNG converter',
    description:
      'Convert WebP to JPG or PNG online for free. Files are processed locally in your browser, with output format selection, JPG quality control and ZIP download.',
    dropTitle: 'Drop WebP files here',
    dropDescription:
      'Choose WebP files up to 50 MB. Add multiple images, preview the result and download JPG/PNG files one by one or as an archive.',
    chooseFiles: 'Choose WebP files',
    outputFormat: 'Output format',
    qualityLabel: 'JPG quality',
    qualityHint:
      'For transparent WebP files saved as JPG, transparent areas are filled with a white background.',
    pngHint:
      'PNG is saved losslessly and keeps transparency, but the result may be larger than the source WebP.',
    sourceLabel: 'WebP',
    resultLabel: 'Result',
    oppositeLabel: 'Need the opposite: JPG/PNG to WebP',
    privacyNote:
      'Files are not sent to a server. WebP is opened through browser image APIs and saved as JPG or PNG with canvas.toBlob.',
    qualityHowTo: 'choose JPG quality from 10% to 100%',
    pngHowTo: 'save PNG without quality settings',
    errorsText: {
      unsupported: 'Only WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      corrupt: 'Could not open this WebP file. It may be corrupted.',
      canvas: 'Could not create a canvas for WebP conversion.',
      conversion: 'Could not convert this WebP file.',
      generic: 'WebP conversion error',
    },
    faq: [
      {
        question: 'Are files sent to a server?',
        answer:
          'No. WebP files are opened and re-saved directly in your browser through canvas. Files stay on your device.',
      },
      {
        question: 'Should I choose JPG or PNG?',
        answer:
          'JPG is usually smaller and works well for photos. PNG keeps transparency and is useful for graphics, but may be larger.',
      },
      {
        question: 'Can I convert several WebP files at once?',
        answer:
          'Yes. Add multiple WebP files, choose JPG or PNG and download results one by one or as a ZIP archive.',
      },
    ],
  },

  avifConverter: {
    eyebrow: 'Local AVIF',
    title: 'AVIF converter online',
    description:
      'Convert JPG, PNG and WebP to AVIF, or save AVIF as JPG/PNG. The file is processed locally through browser image APIs and canvas with no backend.',
    dropTitle: 'Drop an image here',
    dropDescription:
      'Choose an AVIF, JPG, PNG or WebP file up to 50 MB. AVIF support depends on the current browser and is checked during conversion.',
    chooseFile: 'Choose image',
    replaceFile: 'Replace image',
    outputFormat: 'Output format',
    avifQualityLabel: 'AVIF quality',
    jpegQualityLabel: 'JPG quality',
    sourceLabel: 'Source',
    resultLabel: 'Result',
    waitingSource: 'Choose a file',
    waitingResult: 'After conversion',
    privacyNote:
      'The file is not sent to a server. AVIF decode and encode use browser capabilities; if AVIF export is unsupported, you will see a clear error.',
    qualityHowTo: 'choose the output format and quality for AVIF or JPG',
    errorsText: {
      unsupported: 'Only AVIF, JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      unsupportedAvifDecode:
        'This browser could not open the AVIF file. Try updating the browser or saving the file in another format.',
      unsupportedAvifEncode:
        'This browser cannot save AVIF through canvas.toBlob. Choose JPG/PNG or try another browser.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for conversion.',
      conversion: 'Could not convert this image.',
      generic: 'AVIF conversion error',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. The image is opened and saved directly in your browser. No backend or WASM encoder is used.',
      },
      {
        question: 'Why can AVIF export fail?',
        answer:
          'AVIF export depends on canvas.toBlob(image/avif) support in the browser. If it is unavailable, choose JPG or PNG.',
      },
      {
        question: 'Can I open AVIF and save it as a common format?',
        answer:
          'Yes, when the browser can decode the AVIF upload. Choose JPG or PNG as the result and download the converted copy.',
      },
    ],
  },

  tiffToJpg: {
    eyebrow: 'TIFF to JPG',
    title: 'TIFF to JPG converter online',
    description:
      'Convert TIFF and TIF images to JPG for free. Decoding runs locally with UTIF.js and the result is saved through canvas with no file upload.',
    dropTitle: 'Drop TIFF files here',
    dropDescription:
      'Choose TIFF or TIF files up to 50 MB. Add multiple files and download JPG results one by one or as a ZIP archive.',
    chooseFiles: 'Choose TIFF files',
    qualityLabel: 'JPG quality',
    multiPageWarning:
      'This TIFF contains multiple pages or frames. The JPG uses the first frame; other pages are not preserved.',
    privacyNote:
      'Files are not sent to a server. TIFF is decoded locally with UTIF.js, loaded only on this page, and JPG output is created through browser canvas.',
    qualityHowTo: 'choose the JPG size and quality balance',
    errorsText: {
      unsupported: 'Only TIFF and TIF files are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'This image is too large for browser processing. Try a TIFF with a smaller resolution.',
      noImages:
        'No raster image was found in this TIFF. The file may contain only metadata or an unsupported structure.',
      corrupt:
        'Could not open this TIFF. It may be corrupted or use an unsupported format variant.',
      invalidSignature: 'This file does not have a valid TIFF signature.',
      unsupportedVariant: 'This TIFF compression or layout is not supported for safe browser processing.',
      decode: 'Could not decode this TIFF in the browser.',
      memory: 'This TIFF needs more memory than the browser can safely allocate.',
      canvas: 'Could not create a canvas for this TIFF.',
      conversion: 'Could not convert TIFF to JPG.',
      generic: 'TIFF conversion error',
    },
    faq: [
      {
        question: 'Are files sent to a server?',
        answer:
          'No. TIFF is decoded and saved as JPG directly in your browser. The file stays on your device.',
      },
      {
        question: 'Why is only the first frame saved?',
        answer:
          'The MVP is scoped to simple TIFF to JPG conversion. If a TIFF contains multiple pages or frames, the page shows a clear warning and exports the first frame.',
      },
      {
        question: 'Which TIFF files are supported?',
        answer:
          'This MVP accepts uncompressed TIFF with common 8-bit grayscale, RGB, RGBA, palette and CMYK layouts. Compressed variants such as LZW, PackBits, JPEG and CCITT are rejected for safe local processing.',
      },
    ],
  },

  removeExif: {
    eyebrow: 'Photo privacy',
    title: 'Remove EXIF and photo metadata online',
    description:
      'Remove recognized EXIF/XMP, text fields and timestamps from JPG, PNG or WebP. Processing runs locally in your browser with no server upload.',
    dropTitle: 'Drop a photo here',
    dropDescription:
      'Choose one JPG, PNG or WebP up to 50 MB. Unknown and proprietary blocks may remain.',
    chooseFile: 'Choose photo',
    replaceFile: 'Replace photo',
    inspecting: 'Inspecting file...',
    processing: 'Removing metadata...',
    cleanButton: 'Remove metadata',
    previewTitle: 'Photo and cleaned copy',
    sourceLabel: 'Source',
    resultLabel: 'Cleaned copy',
    waitingPreview: 'After cleanup',
    doneStatus: 'Supported metadata removed; the file is ready to download',
    summary: 'Summary',
    metadataLabel: 'Supported metadata',
    metadataYes: 'Found',
    metadataNo: 'Not found',
    metadataLabels: {
      exif: 'EXIF',
      xmp: 'XMP',
      text: 'Text fields',
      time: 'Modification time',
      colorProfile: 'ICC color profile',
    },
    colorProfilePreserved: 'Preserved',
    jpegLossyNote:
      'JPG is re-saved through canvas at 95% quality. This is a lossy operation: file size and some pixel values may change.',
    originalSize: 'Original size',
    cleanedSize: 'After cleanup',
    privacyNote:
      'The file is not sent to a server. This is not forensic cleanup: unknown and proprietary blocks may remain. The tool provides no legal or compliance guarantee.',
    howTo: [
      'Choose a JPG, PNG or WebP file on your device.',
      'Review the detected metadata types and ICC profile preservation.',
      'Run cleanup and download the cleaned copy.',
    ],
    errorsText: {
      unsupported:
        'Only JPG, JPEG, PNG and WebP are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeDimensions:
        'The image dimensions are too large. Maximum: 36 megapixels and 32768 pixels per side.',
      corrupt: 'Could not inspect this image. Its container may be corrupted.',
      canvas: 'Could not create a canvas for EXIF cleanup.',
      cleanup: 'Could not create a cleaned copy.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. Inspection and cleanup run in your browser. The file stays on your device.',
      },
      {
        question: 'What exactly is removed?',
        answer:
          'JPG metadata is removed by creating a new JPG copy. PNG cleanup removes eXIf, tEXt, zTXt, iTXt and tIME; WebP cleanup removes EXIF and XMP. Container cleanup preserves ICC profiles and image data for PNG/WebP. JPEG is re-saved through canvas, so ICC profile preservation is not guaranteed.',
      },
      {
        question: 'Does this remove every trace and hidden field?',
        answer:
          'No. Unknown and proprietary blocks may remain. This is not a forensic tool and provides no legal or compliance guarantee.',
      },
    ],
  },

  physicalPhotoSize: {
    eyebrow: 'Print size and PPI',
    title: 'Photo size in centimetres and PPI',
    description:
      'Read pixel dimensions and recorded density, then calculate the intended print size in centimetres, millimetres and inches.',
    dropTitle: 'Drop one photo here',
    dropDescription:
      'One static JPG, PNG or WebP up to 50 MB, 36 MP and 32,768 px per side. APNG and animated WebP are rejected.',
    chooseFile: 'Choose photo',
    inspecting: 'Inspecting photo...',
    cancel: 'Cancel',
    reset: 'Open another',
    pixelsTitle: 'Pixel dimensions',
    pixelsDescription: 'Pixels describe the image grid but do not define centimetres by themselves.',
    densityTitle: 'Recorded density',
    states: {
      found: 'PPI found',
      not_recorded: 'Not recorded',
      aspect_ratio_only: 'Pixel ratio only',
      asymmetric: 'X and Y differ',
      conflicting_sources: 'Sources conflict',
      unsupported_metadata_source: 'Could not confirm',
    },
    stateDescriptions: {
      found: 'One consistent source with a physical unit was found.',
      not_recorded: 'No confirmed density record exists. No default is invented.',
      aspect_ratio_only: 'An X/Y ratio is recorded without inches or centimetres, so it is not PPI.',
      asymmetric: 'X and Y density differ and are not averaged.',
      conflicting_sources: 'Multiple unit-bearing sources disagree, so none is silently selected.',
      unsupported_metadata_source: 'A malformed or unsupported density source exists, so absence cannot be claimed.',
    },
    sourcesTitle: 'Raw metadata records',
    noSources: 'No JFIF, Exif or pHYs density record was found.',
    source: 'Source',
    unit: 'Unit',
    sourceNames: {
      jfif: 'JPEG JFIF',
      exif: 'JPEG Exif',
      png_phys: 'PNG pHYs',
      png_exif: 'PNG eXIf',
      webp_exif: 'WebP Exif',
      xmp: 'Unsupported metadata source',
    },
    units: {
      ppi: 'per inch',
      ppcm: 'per centimetre',
      ppm: 'pixels per metre',
      unitless: 'no physical unit',
      invalid: 'could not confirm',
    },
    asymmetricWarning: 'X and Y remain separate: this can mean non-square pixels or inconsistent metadata. You can explicitly select a PPI for calculation.',
    conflictWarning: 'Sources disagree. No automatic size is calculated until you explicitly select a PPI.',
    ppiTitle: 'PPI for calculation',
    ppiDescription: 'PPI means image pixels per inch. DPI means output-device dots per inch; the colloquial “photo DPI” query is explained as image PPI here.',
    customPpi: 'Custom PPI from 1 to 2400',
    apply: 'Apply',
    useMetadata: 'Use file value',
    userSelected: 'The selected PPI is user-provided, not detected in the file.',
    detected: 'Using confirmed PPI from the file.',
    choosePpi: 'Choose 72, 96, 150, 300 or 600 PPI, or enter a custom value.',
    sizeTitle: 'Intended physical size',
    formula: 'Inches = pixels / PPI; centimetres = inches × 2.54; millimetres = inches × 25.4.',
    centimeters: 'Centimetres',
    millimeters: 'Millimetres',
    inches: 'Inches',
    calculationNote: 'A metadata-only change does not add detail or improve quality. Resampling changes the pixel grid and can change sharpness, bytes and quality.',
    related: [
      { href: '/izmenit-razmer-foto', title: 'Change pixels', description: 'Changes raster width and height, not just the print-size calculation.' },
      { href: '/posmotret-exif', title: 'View EXIF', description: 'Shows a broader metadata, camera and privacy-field inventory.' },
      { href: '/udalit-exif', title: 'Remove EXIF', description: 'Creates a separate cleaned copy instead of a read-only inspection.' },
    ],
    howToTitle: 'How to find photo size in centimetres',
    howTo: [
      'Choose one static JPG, PNG or WebP from your device.',
      'Review pixels, source and recorded-density state.',
      'Use confirmed PPI or explicitly choose your own, then read all three units.',
    ],
    errors: {
      unsupported: 'Only static JPG/JPEG, PNG and WebP are supported.',
      tooLarge: 'The file exceeds the 50 MB limit.',
      dimensions: 'The image exceeds 36 MP or 32,768 px on one side.',
      animated: 'APNG and animated WebP are not supported. Choose one static frame.',
      invalid: 'The container is corrupt, truncated or structurally inconsistent.',
      failed: 'Could not safely inspect the image in this browser.',
      ppi: 'Enter a finite PPI value from 1 to 2400.',
    },
    faq: [
      { question: 'Are photo DPI and PPI the same?', answer: 'No. PPI means image pixels per inch; DPI means printer dots per inch. The colloquial “photo DPI” query usually refers to recorded PPI.' },
      { question: 'Will the tool assume 72 or 96 when PPI is missing?', answer: 'No. No default is invented: choose a preset or enter PPI explicitly.' },
      { question: 'Will photo quality change?', answer: 'No. This tool saves and changes nothing. A metadata-only change adds no detail; resampling is a separate operation that changes pixels.' },
      { question: 'Is the photo sent to a server?', answer: 'No. Bytes go to a disposable browser worker and stay on the device. Analytics receives only bounded invoke, success or error without names, dimensions, PPI or metadata.' },
    ],
  },

  metadataViewer: {
    eyebrow: 'Photo privacy check',
    title: 'View EXIF and photo metadata online',
    description:
      'Inspect EXIF, IPTC, XMP and sensitive JPG, TIFF or HEIC fields locally in your browser. The file and detected data are not sent to a server.',
    dropTitle: 'Drop a photo here',
    dropDescription:
      'Choose one JPG, TIFF or HEIC up to 50 MB. Support depends on the container: HEIC XMP and IPTC are explicitly marked unavailable.',
    chooseFile: 'Choose photo',
    inspecting: 'Reading metadata...',
    cancel: 'Cancel inspection',
    reset: 'Open another',
    resultsTitle: 'Detected metadata',
    formatLabel: 'Format',
    available: 'Found',
    notFound: 'Not found',
    unsupported: 'Unsupported',
    noMetadata: 'No recognized fields were found. Unknown or proprietary blocks may still exist.',
    copyJson: 'Copy JSON',
    copied: 'JSON copied',
    privacyTitle: 'Privacy flags',
    noPrivacyFlags: 'No selected privacy flags were found in recognized fields.',
    openMap: 'Open coordinates on a map',
    removeExifCta: 'Remove EXIF from a copy',
    privacyNote:
      'Processing runs in a dedicated worker. Coordinates are not requested from a map or sent anywhere until you explicitly click the link. The tool does not verify metadata authenticity and is not forensic analysis.',
    groups: {
      camera: 'Camera and capture',
      date: 'Date and time',
      orientation: 'Orientation',
      gps: 'GPS and location',
      serial: 'Serial numbers and author',
      software: 'Software and processing',
      thumbnail: 'Embedded thumbnail',
      other: 'Other recognized fields',
    },
    privacyFlags: {
      gps: 'Coordinates or location data were found',
      serial: 'A serial number, owner or author was found',
      software: 'Software or firmware details were found',
      embedded_thumbnail: 'The file contains a separate embedded thumbnail',
    },
    howTo: [
      'Choose a JPG, TIFF or HEIC up to 50 MB.',
      'Review grouped fields and highlighted privacy flags.',
      'Copy JSON or continue to remove EXIF from a separate copy.',
    ],
    errorsText: {
      unsupported: 'Only JPG/JPEG, TIFF/TIF and HEIC/HEIF are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      invalid: 'Could not read this container. It is corrupt or uses an unsupported variant.',
      failed: 'Could not safely read metadata in this browser.',
      copy: 'Could not copy JSON. Select the data manually or allow clipboard access.',
    },
    faq: [
      {
        question: 'Are the file and metadata sent to a server?',
        answer:
          'No. Parsing runs locally in a dedicated browser worker. Analytics receives only bounded invoke, success or error events without file names, coordinates or contents.',
      },
      {
        question: 'Which metadata formats are supported?',
        answer:
          'JPG and TIFF are inspected for EXIF/GPS, IPTC and XMP. HEIC uses confirmed EXIF/GPS support; XMP and IPTC are marked unsupported rather than absent.',
      },
      {
        question: 'Does this guarantee there is no hidden data?',
        answer:
          'No. Unknown and proprietary blocks may remain undetected. The tool shows recognized fields but is not an antivirus or forensic inspection.',
      },
    ],
  },


  compressImage: {
    eyebrow: 'Image compression',
    title: 'Compress image online',
    description:
      'Upload JPG or WebP, choose quality or a file-size limit, and download the result. PNG uses honest browser re-save. Everything runs locally with no image upload.',
    formats: 'JPG, PNG, WebP',
    dropTitle: 'Drop images here',
    dropDescription:
      'Choose JPG, PNG or WebP files up to 50 MB. Add multiple files, compare before and after sizes, and download everything as a ZIP archive.',
    chooseFiles: 'Choose images',
    compressButton: 'Compress',
    updatePreviewButton: 'Update preview',
    compressing: 'Compressing...',
    modeLabel: 'Compression mode',
    qualityMode: 'By quality',
    targetMode: 'Target file size',
    qualityLabel: 'JPG/WebP quality',
    qualityHint:
      'The slider affects JPG and WebP. Lower quality usually means a smaller file.',
    targetPresetLabel: 'File limit',
    customTarget: 'Custom',
    targetValueLabel: 'Target size',
    targetHint:
      'For JPG and WebP, the browser performs a bounded quality search and reduces dimensions when needed. Appearance may change.',
    invalidTarget: 'Enter a size from 10 KB to 50 MB.',
    pngOnlyHint:
      'Only PNG files are queued: the browser will re-save them without a quality setting.',
    pngNotice:
      'PNG is saved losslessly through browser canvas. If the source PNG is already optimized, the file may barely shrink or become slightly larger.',
    pngNoSavings:
      'No savings for this PNG: browser re-save does not always reduce already optimized PNG files.',
    pngTargetNotice:
      'An exact target is unavailable for PNG: it is only re-saved losslessly and is never marked as reaching the requested size.',
    sourceLabel: 'Before',
    resultLabel: 'After',
    waitingPreview: 'After compression',
    originalSize: 'Original size',
    compressedSize: 'After compression',
    actualQuality: 'Actual quality',
    targetReached: 'Target reached',
    bestFound: 'Best result found',
    downloadBest: 'Download best',
    targetUnreachable:
      'The target is unreachable at minimum quality and a 320 px minimum long side. This is the actual minimum within supported guardrails.',
    targetAttemptLimit:
      'The attempt limit stopped the search. This is the best result found, not a proven minimum.',
    targetTimeLimit:
      'The cooperative time budget stopped the search. This is the best result found, not a proven minimum.',
    privacyNote:
      'Files are not sent to a server. JPG, PNG and WebP are opened and saved through browser image APIs and canvas.toBlob.',
    qualityHowTo:
      'choose the size and quality balance for JPG/WebP; PNG is saved without a separate quality setting',
    errorsText: {
      unsupported: 'Only JPG, PNG and WebP images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooLargeImage:
        'The image is too large for local canvas processing. Maximum: 36 megapixels and 32768 px per side.',
      unsupportedWebp:
        'This browser cannot save WebP through canvas. Try updating your browser or use another one.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      canvas: 'Could not create a canvas for compression.',
      compression: 'Could not compress this image.',
      invalidTarget: 'Enter a valid file-size limit.',
      generic: 'Image compression error',
    },
    howToTitle: 'How to compress an image',
    howTo: [
      'Choose one or more JPG, PNG or WebP files up to 50 MB.',
      'Keep manual quality or choose target file size and enter a KB/MB limit.',
      'Check the actual size, quality and dimensions, then download a file or ZIP.',
    ],
    faq: [
      {
        question: 'Are files sent to a server?',
        answer:
          'No. Images are opened and re-saved locally in your browser through canvas. Files stay on your device.',
      },
      {
        question: 'Will the exact target size always be reached?',
        answer:
          'No. Success appears only when a JPG or WebP is at or below the limit. The search may reduce quality and dimensions; if minimum guardrails are still too large, the page shows the actual minimum honestly.',
      },
      {
        question: 'Why is exact target size unavailable for PNG?',
        answer:
          'Browser canvas saves PNG without equivalent quality control. PNG is re-saved losslessly but never receives a false target-reached status.',
      },
      {
        question: 'Can I compress several files at once?',
        answer:
          'Yes. Add multiple JPG, PNG or WebP files and download results one by one or as a ZIP archive.',
      },
    ],
  },

  svgConverter: {
    eyebrow: 'SVG to PNG/JPG',
    title: 'SVG to PNG and JPG converter',
    description:
      'Upload an SVG, choose PNG or JPG, keep the original size or enter a custom width and height. Processing runs locally in your browser.',
    dropTitle: 'Drop an SVG here',
    dropDescription:
      'Choose one SVG file up to 10 MB.',
    chooseFile: 'Choose SVG',
    replaceFile: 'Replace SVG',
    maxFileSize: 'Up to 10 MB',
    settings: 'Conversion',
    outputFormat: 'Output format',
    keepOriginalSize: 'Original size',
    keepAspect: 'Keep aspect ratio',
    width: 'Width',
    height: 'Height',
    backgroundColor: 'JPG background',
    convert: 'Convert',
    processing: 'Preparing file...',
    download: 'Download result',
    sourceLabel: 'SVG',
    resultLabel: 'Result',
    waitingPreview: 'After conversion',
    notReady: 'Not ready',
    summary: 'Summary',
    originalSize: 'Source file',
    targetSize: 'Output size',
    resultSize: 'After conversion',
    privacyNote:
      'The file is not sent to a server. SVG is validated as XML, unsafe constructs are blocked, then a safe copy is drawn to canvas in your browser.',
    howTo: [
      'Choose one SVG file on your device.',
      'Choose PNG or JPG, keep the original size or enter width and height.',
      'Click convert and download the finished raster image.',
    ],
    errorsText: {
      unsupported: 'Only SVG files are supported.',
      tooLarge: 'The file is too large. Maximum size is 10 MB.',
      invalidSvg: 'Could not read the SVG. Check that this is a valid XML/SVG file.',
      unsafeSvg:
        'This SVG contains scripts, event handlers or other unsafe constructs. The file is not processed.',
      externalResource:
        'This SVG references external resources or CSS imports. Use a self-contained SVG for safe local conversion.',
      unsupportedSvg:
        'The browser could not render this SVG. The file may use unsupported SVG/CSS features.',
      canvas: 'Could not create a canvas for this output size.',
      conversion: 'Could not convert this SVG.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. The SVG is read, checked and rendered to canvas directly in your browser. The file stays on your device.',
      },
      {
        question: 'Why do some SVG files fail?',
        answer:
          'For safety, scripts, event handlers, foreignObject, external images/fonts and CSS imports are blocked. Browser canvas may also miss some complex SVG/CSS features.',
      },
      {
        question: 'What happens to transparency in JPG?',
        answer:
          'JPG does not support transparency, so transparent areas are filled with the selected background. Use PNG when you need transparency.',
      },
    ],
  },

  heicViewer: {
    howToTitle: 'How to use',
    eyebrow: 'HEIC viewer',
    title: 'Open HEIC online',
    description:
      'Open one HEIC or HEIF file online and view the photo directly in your browser. Download a JPG or PNG copy if you need one.',
    acceptedFormats: 'HEIC, HEIF',
    dropTitle: 'Drop a HEIC or HEIF file to open it',
    dropDescription: 'Choose one HEIC or HEIF up to 50 MB.',
    chooseFile: 'Open HEIC/HEIF',
    loading: 'Opening...',
    previewTitle: 'HEIC opened in the browser',
    previewAlt: 'HEIC preview',
    dimensions: 'Dimensions',
    downloadJpg: 'Download JPG copy',
    downloadPng: 'Download PNG copy',
    exportingJpg: 'Preparing JPG...',
    exportingPng: 'Preparing PNG...',
    multiFrameWarning:
      'This HEIC/HEIF contains multiple frames. The preview and export use the first frame; Live Photo/video content is not preserved.',
    privacyNote:
      'The file is not sent to a server. HEIC is decoded locally with the browser-side heic2any library; secondary JPG/PNG export runs through canvas in your browser.',
    relatedToolsTitle: 'More HEIC actions',
    relatedTools: [
      {
        title: 'HEIC to JPG, PNG or WebP',
        description: 'Convert up to 10 files and pick the format.',
        href: '/heic-v-jpg',
      },
      {
        title: 'Compress image',
        description: 'Reduce the size of a JPG copy.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'View EXIF',
        description: 'Check the date, camera and location in HEIC.',
        href: '/posmotret-exif',
      },
    ],
    howTo: [
      'Click "Open HEIC/HEIF" or drop one HEIC/HEIF file on the page.',
      'Wait for the local preview and view the image in your browser.',
      'Optionally download a secondary JPG or PNG copy.',
    ],
    errorsText: {
      unsupported: 'Only HEIC/HEIF images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image: 'The photo is too large to open in the browser.',
      decode_failure: 'The browser could not show the decoded HEIC. Try another browser.',
      corrupt:
        'Could not open this HEIC/HEIF file. It may be corrupted, use an unsupported format variant or only be available as a multi-frame sequence.',
      canvas: 'Could not prepare the canvas for download.',
      conversion: 'Could not prepare the file for download.',
      generic: 'HEIC opening error',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. HEIC/HEIF is decoded in your browser. Preview and JPG/PNG downloads are prepared locally on your device.',
      },
      {
        question: 'How is this different from HEIC to JPG?',
        answer:
          'The primary action here is opening and viewing a HEIC file. JPG or PNG conversion is available as an extra download from the preview.',
      },
      {
        question: 'When should I use a HEIC converter?',
        answer:
          'If you need to process up to 10 files or choose JPG, PNG or WebP, use the single HEIC converter.',
      },
      {
        question: 'What happens to Live Photo or multi-frame HEIC files?',
        answer:
          'The MVP shows the first frame. If the file has multiple frames, the page displays a warning; Live Photo video content and metadata are not preserved.',
      },
    ],
  },

  jpgViewer: {
    howToTitle: 'How to use',
    eyebrow: 'JPG viewer',
    title: 'Open JPG online',
    description:
      'Upload one JPG or JPEG file, view the image in your browser and check name, weight, dimensions, MIME type and format. The file stays on your device.',
    acceptedFormats: 'JPG, JPEG',
    dropTitle: 'Drop a JPG or JPEG here',
    dropDescription:
      'Choose one JPG/JPEG up to 50 MB.',
    chooseFile: 'Choose JPG',
    loading: 'Opening...',
    previewTitle: 'Image preview',
    previewAlt: 'JPG preview',
    dimensions: 'Dimensions',
    relatedToolsTitle: 'Related tools',
    privacyNote:
      'The file is not sent to a server. JPG is opened through the browser native image API; preview and facts are calculated locally.',
    relatedTools: [
      {
        title: 'JPG -> PNG',
        description: 'Save the opened photo as PNG without JPG compression.',
        href: '/konverter-izobrazheniy',
      },
      {
        title: 'JPG -> WebP',
        description: 'Prepare a WebP copy for a website or blog.',
        href: '/v-webp',
      },
      {
        title: 'Compress JPG',
        description: 'Reduce file weight with quality control.',
        href: '/szhat-izobrazhenie',
      },
      {
        title: 'Resize image',
        description: 'Set a new image width and height.',
        href: '/izmenit-razmer-foto',
      },
      {
        title: 'Remove EXIF',
        description: 'Clean metadata before publishing.',
        href: '/udalit-exif',
      },
    ],
    howTo: [
      'Choose one JPG or JPEG file on your device.',
      'Check the preview, dimensions, file weight and MIME type.',
      'Use zoom/fit controls or continue to compression, resize, EXIF cleanup or conversion.',
    ],
    errorsText: {
      unsupported: 'Only JPG and JPEG images are supported.',
      too_large: 'The file is too large. Maximum size is 50 MB.',
      too_large_image:
        'This image is too large to open in the browser. Try a file with a smaller resolution.',
      corrupt: 'Could not open the image. The file may be corrupted.',
      decode_failure:
        'The browser could not decode this JPG. Try another browser or resave the image.',
    },
    faq: [
      {
        question: 'Is the file sent to a server?',
        answer:
          'No. JPG is opened locally by the browser: the file is not uploaded and stays on your device.',
      },
      {
        question: 'Can I open a JPEG with the .jpeg extension?',
        answer: 'Yes. The page accepts .jpg and .jpeg files with the image/jpeg MIME type.',
      },
      {
        question: 'What can I do after viewing it?',
        answer:
          'You can continue to JPG to PNG or WebP conversion, compression, resizing or EXIF metadata removal.',
      },
    ],
  },

  heicToJpg: {
    eyebrow: 'HEIC to JPG, PNG or WebP',
    title: 'HEIC to JPG, PNG and WebP converter',
    description:
      'Convert HEIC and HEIF photos to JPG online for free, or choose PNG and WebP. Processing runs locally in your browser: files are not sent to a server.',
    dropTitle: 'Drop HEIC or HEIF files here',
    dropDescription:
      'Add up to 10 HEIC/HEIF files of up to 50 MB each, choose JPG, PNG or WebP, and download results separately or as a ZIP archive.',
    chooseFiles: 'Choose HEIC files',
    outputLabel: 'Output format',
    outputHelp:
      'JPG suits regular photos, PNG adds no JPEG compression, and WebP is convenient for web publishing. Each HEIC uses the shared local decode pipeline.',
    viewerLink: 'Only need to open HEIC? Go to the viewer',
    skippedNotice:
      'Some files were skipped: only HEIC/HEIF files up to 50 MB per file are supported.',
    multiFrameWarning:
      'This file contains multiple frames. The output uses the first frame; Live Photo/video content is not preserved.',
    privacyNote:
      'Files are not sent to a server. HEIC is decoded locally with the browser-side heic2any library; the shared decoder creates JPG/PNG and canvas additionally encodes WebP.',
    qualityHowTo: 'choose the JPG size and quality balance',
    howTo: [
      'Add up to 10 HEIC or HEIF files of up to 50 MB each.',
      'Choose JPG, PNG or WebP; adjust quality for JPG and WebP when needed.',
      'Run the local conversion and download results separately or as a ZIP archive.',
    ],
    errorsText: {
      unsupported: 'Only HEIC/HEIF images are supported.',
      tooLarge: 'The file is too large. Maximum size is 50 MB.',
      tooMany: 'You can add up to 10 files per run.',
      corrupt:
        'Could not open this HEIC/HEIF file. It may be corrupted or use an unsupported format variant.',
      conversion:
        'Could not convert this HEIC file. Try another file or export the photo as a compatible JPG on your device.',
      generic: 'HEIC conversion error',
    },
    faq: [
      {
        question: 'Are files sent to a server?',
        answer:
          'No. HEIC/HEIF is decoded and saved as the selected JPG, PNG or WebP directly in your browser. Filenames, EXIF and file content are not sent to analytics.',
      },
      {
        question: 'How should I choose between JPG, PNG and WebP?',
        answer:
          'JPG is the default and usually suits photos. PNG adds no JPEG compression but is often larger. WebP requires canvas encoding support in the browser.',
      },
      {
        question: 'What happens to Live Photo or multi-frame HEIC files?',
        answer:
          'The MVP saves one selected-format file from the first frame. Animation, Live Photo video content, EXIF and the original color profile are not preserved. Some HEIF variants are unsupported.',
      },
    ],
  },

  htmlToImage: {
    title: 'HTML to image online',
    description:
      'Paste HTML or upload a .html file, choose the canvas size and download PNG or JPG. Rendering runs locally in the browser through sandbox preview and canvas export.',
    summaryTitle: 'MVP',
    privacyNote:
      'HTML is not sent to a server. Scripts and external resources are cleaned before preview and export.',
    inputTypeLabel: 'Input',
    inputTypeValue: 'Pasted HTML / .html file',
    processingLabel: 'Processing',
    processingValue: 'Local in browser',
    outputLabel: 'Format',
    resultSizeLabel: 'File size',
    editorTitle: 'HTML',
    editorDescription:
      'Works best for cards, banners, email blocks and small static snippets.',
    htmlLabel: 'HTML code',
    uploadHtml: 'Upload .html',
    resetExample: 'Reset example',
    settingsTitle: 'Export',
    settingsDescription:
      'Set the image size and output format. Large canvases are limited for stable browser processing.',
    widthLabel: 'Width',
    heightLabel: 'Height',
    formatLabel: 'Format',
    previewTitle: 'HTML preview',
    renderButton: 'Create image',
    rendering: 'Rendering...',
    downloadButton: 'Download',
    howTo: [
      'Paste HTML into the editor or upload a local .html file.',
      'Choose width, height and PNG or JPG output.',
      'Render the HTML and download the finished image.',
    ],
    errorsText: {
      emptyHtml: 'Paste HTML before exporting.',
      invalidSize: 'Set a valid width and height.',
      tooLargeCanvas:
        'The canvas is too large for browser processing. Reduce width or height.',
      renderFailed:
        'Could not render this HTML. Check the markup and remove unsupported external resources.',
      exportFailed: 'Could not save the image.',
      unsupportedFile: 'Only .html files are supported.',
      readFailed: 'Could not read the HTML file.',
    },
    faq: [
      {
        question: 'Can I paste a website URL?',
        answer:
          'Not in the MVP. Public URL capture needs a server-side browser, timeouts and abuse protection. This tool supports pasted HTML and local .html files.',
      },
      {
        question: 'Is HTML sent to a server?',
        answer:
          'No. HTML is cleaned, shown in a sandbox preview and exported to PNG/JPG directly in the browser.',
      },
      {
        question: 'Why can external images or fonts disappear?',
        answer:
          'The tool does not act as a crawler and does not fetch external resources from HTML. This reduces CORS, privacy and abuse risks.',
      },
    ],
  },

  onlinePaint: {
    eyebrow: 'Blank canvas · local',
    title: 'Draw online on a canvas',
    description:
      'Create a diagram, note or illustration on a blank canvas. Use a brush, shapes, arrows, text, undo and export without sending content to a server.',
    toolbar: 'Drawing toolbar',
    canvasSize: 'Canvas size',
    presets: {
      default: 'Standard',
      square: 'Square',
      wide: 'Wide 16:9',
      a4: 'A4 portrait',
    },
    tool: 'Tool',
    tools: {
      brush: 'Brush',
      eraser: 'Eraser',
      line: 'Line',
      rectangle: 'Rectangle',
      ellipse: 'Ellipse',
      arrow: 'Arrow',
      text: 'Text',
    },
    color: 'Color',
    background: 'Background',
    strokeWidth: 'Stroke width',
    textValue: 'Label text',
    textSize: 'Text size',
    undo: 'Undo',
    redo: 'Redo',
    clear: 'Clear canvas',
    opaque: 'Opaque background',
    commandCount: 'Actions',
    download: 'Download:',
    exporting: 'Preparing locally…',
    privacy:
      'Your drawing and text stay in the browser. Only ordinary anonymous usage events without canvas content are sent.',
    howToTitle: 'How to draw online',
    howTo: [
      'Choose a blank canvas size, background color and tool.',
      'Draw with a mouse, pen or finger; add shapes, arrows and text.',
      'Use undo and redo, then download PNG, JPG or WebP.',
    ],
    faqTitle: 'Drawing questions',
    faq: [
      {
        question: 'Are the drawing and text sent to a server?',
        answer: 'No. The canvas is created and exported locally in your browser without uploading its content to a backend.',
      },
      {
        question: 'What size is the downloaded image?',
        answer: 'The file uses the actual selected canvas dimensions. Scaling the on-screen preview does not change export resolution.',
      },
      {
        question: 'What is the difference between PNG, JPG and WebP?',
        answer: 'PNG suits crisp graphics, JPG is broadly compatible and often compact, and WebP may be smaller. The canvas always uses your selected opaque background.',
      },
    ],
    relatedTitle: 'Other tools',
    related: [
      { href: '/izmenit-razmer-foto', label: 'Resize an image' },
      { href: '/obrezat-foto', label: 'Crop an image' },
      { href: '/vybrat-cvet-s-kartinki', label: 'Pick a color from an image' },
    ],
    errors: {
      canvas: 'The drawing canvas is unavailable in this browser.',
      export: 'The drawing could not be saved. Try another format.',
      unsupportedFormat: 'This browser does not support the selected export format.',
      textRequired: 'Enter text, then click the canvas.',
      commandLimit: 'The safe action limit has been reached. Download the drawing or clear the canvas.',
      pointLimit: 'The safe point limit has been reached. Download the drawing or clear the canvas.',
    },
  },

  photoCollage: {
    howTo: [
      'Add 2 to 12 JPG, PNG or WebP photos.',
      'Choose the grid, order, ratio, gaps and focal point for each slot.',
      'Create the collage locally and download JPG or PNG.',
    ],
    faq: [
      {
        question: 'Are photos sent to a server?',
        answer:
          'No. Source files stay in the browser and the full-size collage is rendered locally in a dedicated worker.',
      },
      {
        question: 'Can I adjust each photo crop?',
        answer:
          'Yes. Every slot has an independent horizontal and vertical focal point.',
      },
      {
        question: 'How many photos can I add?',
        answer:
          'From 2 to 12. Safe total pixel, memory and export time limits also apply.',
      },
    ],
  },

  imageGifMaker: {
    howTo: [
      'Add 2–20 static JPG, PNG or WebP images.',
      'Order frames, choose global or per-frame delays, and set looping.',
      'Choose the canvas and fit, then create and download the GIF.',
    ],
    faq: [
      { question: 'Are photos uploaded?', answer: 'No. Validation, preview and GIF encoding run locally in your browser.' },
      { question: 'Why can colours change?', answer: 'GIF uses a limited palette and binary transparency. This tool uses up to 255 colours without dithering.' },
      { question: 'Can I add an existing GIF or video?', answer: 'No. Only static JPG, PNG and WebP images are accepted; animated images and video are rejected.' },
    ],
  },

  socialMediaResize: {
    howTo: [
      'Add one to four local JPG, PNG or WebP files.',
      'Set each source focal point and optionally pan an individual crop.',
      'Choose sizes, generate outputs sequentially, then download files or ZIP.',
    ],
    faq: [
      {
        question: 'Are images uploaded to a server?',
        answer: 'No. Validation, decoding, crop, resize and ZIP generation stay in the browser.',
      },
      {
        question: 'Are all sizes official?',
        answer: 'No. The UI separates official requirements, imgify recommendations and derived safe zones.',
      },
      {
        question: 'Is the image appearance after publishing guaranteed?',
        answer: 'No. Imgify guarantees the exported canvas and bytes; a third party may reprocess it.',
      },
    ],
  },

  blog: {
    breadcrumb: 'Blog',
    title: 'imgify blog',
    description:
      'Practical notes about image formats, file weight, local processing and preparing pictures for websites.',
    languageAria: 'Article language',
    postLanguageAria: 'Article language',
    empty: 'There are no posts in this language yet.',
    notFoundTitle: 'Post not found',
    notFoundDescription: 'This imgify blog post does not exist or the link is outdated.',
    relatedTools: 'Related tools',
    localeName: {
      ru: 'Русский',
      en: 'English',
    },
  },

  seo: {
    onlinePaint: {
      title: 'Draw online for free on a canvas - imgify.ru',
      description:
        'Draw online on a blank canvas for free with a brush, eraser, lines, shapes, arrows, text, undo and PNG, JPG or WebP export in your browser.',
    },
    index: {
      title: 'Free online image tools - imgify.ru',
      description:
        'Free local image tools: PNG to JPG and JPG to PNG converter. No sign-up and no files sent to a server. Runs in your browser.',
    },
    imageConverter: {
      title: 'PNG and JPG image converter online - imgify.ru',
      description:
        'Convert PNG to JPG and JPG to PNG for free with direction selection, JPG quality control and local batch processing without server uploads.',
    },
    cropImage: {
      title: 'Crop photo online for free - imgify.ru',
      description:
        'Crop JPG, PNG or WebP online for free. Free crop, 1:1, 4:3, 16:9 and 3:4 presets, local browser processing and no file upload.',
    },
    rotateImage: {
      title: 'Rotate photo online for free - imgify.ru',
      description:
        'Rotate JPG, PNG or WebP online for free. 90, 180 and 270 degree rotation, horizontal and vertical flip, local browser processing and no server upload.',
    },
    blurImage: {
      title: 'Blur photo online for free - imgify.ru',
      description:
        'Blur or pixelate areas on JPG, PNG or WebP images for free. Manual rectangle selection, effect strength control and local processing with no server upload.',
    },
    watermarkImage: {
      title: 'Watermark photo online for free - imgify.ru',
      description:
        'Add a text watermark to JPG, PNG or WebP for free. Position, opacity, size, rotation, output format selection and local processing with no server upload.',
    },
    photo3x4: {
      title: '3x4 photo online for free - imgify.ru',
      description:
        'Make a 3x4 photo online for free: fixed 3:4 frame, 300x400, 600x800 or custom size, transparency background and local processing with no server upload.',
    },
    photoCollage: {
      title: 'Photo collage maker online for free - imgify.ru',
      description:
        'Make a collage from 2–12 photos with grids, ordering, crop, gaps, background, JPG and PNG. Files stay local in your browser.',
    },
    imageGifMaker: {
      title: 'Create a GIF from photos online for free - imgify.ru',
      description:
        'Build a GIF from 2–20 JPG, PNG or WebP images with ordering, delays, looping, fit and local encoding without uploading files.',
    },
    compressImage: {
      title: 'Compress image online for free - imgify.ru',
      description:
        'Compress JPG and WebP by quality or target file size, with honest PNG browser re-encode. Local preview and ZIP with no server upload.',
    },
    removeExif: {
      title: 'Remove EXIF and photo metadata online for free - imgify.ru',
      description:
        'Remove recognized EXIF, XMP, text metadata and timestamps from JPG, PNG and WebP locally in your browser with no file upload.',
    },
    metadataViewer: {
      title: 'View EXIF and photo metadata online - imgify.ru',
      description:
        'View EXIF, IPTC, XMP, GPS and privacy-sensitive JPG, TIFF or HEIC fields locally in your browser without sending the file or metadata to a server.',
    },
    physicalPhotoSize: {
      title: 'Photo size in centimetres and PPI online - imgify.ru',
      description:
        'Read photo pixels and print size, inspect JFIF, Exif or PNG pHYs PPI and calculate locally without uploading the image to a server.',
    },
    imageToWebp: {
      title: 'JPG and PNG to WebP converter online - imgify.ru',
      description:
        'Convert JPG, JPEG and PNG to WebP for free. Local browser processing, WebP quality control, result preview and ZIP archive.',
    },
    webpConverter: {
      title: 'WebP to JPG and PNG converter online - imgify.ru',
      description:
        'Convert WebP to JPG or PNG for free. Local browser processing, output format selection, JPG quality, preview and ZIP archive.',
    },
    avifConverter: {
      title: 'AVIF converter online for free - imgify.ru',
      description:
        'Convert JPG, PNG and WebP to AVIF or AVIF to JPG/PNG for free. Local browser processing, preview, size comparison and no backend.',
    },
    tiffToJpg: {
      title: 'TIFF to JPG converter online - imgify.ru',
      description:
        'Convert TIFF and TIF to JPG for free. Local processing with UTIF.js, JPG quality, batch downloads, ZIP archive and no server upload.',
    },
    svgConverter: {
      title: 'SVG to PNG and JPG converter online - imgify.ru',
      description:
        'Convert SVG to PNG or JPG for free. Local browser processing, custom size controls, JPG background color and clear errors for unsupported SVG files.',
    },
    htmlToImage: {
      title: 'HTML to image converter online - imgify.ru',
      description:
        'Convert HTML to PNG or JPG for free. Pasted HTML and .html file input, local sandbox preview, canvas export and download without URL capture.',
    },
    resizeImage: {
      title: 'Resize photo online for free - imgify.ru',
      description:
        'Downsize JPG, PNG or WebP online for free. Width, height, aspect ratio lock, 50% and 25% presets, local browser processing and no server upload.',
    },
    socialMediaResize: {
      title: 'Social media image resizer online for free - imgify.ru',
      description:
        'Prepare images for VK, Telegram, YouTube and avatars with focal crops, six sourced sizes, individual JPG files and a local ZIP without server upload.',
    },
    upscaleImage: {
      title: 'Upscale photo online for free - imgify.ru',
      description:
        'Enlarge JPG, PNG or WebP online for free: 2x, 3x, 4x or custom size. Local canvas scaling with no AI and no file upload.',
    },
    faviconGenerator: {
      title: 'Favicon generator online for free - imgify.ru',
      description:
        'Create a favicon package from PNG, JPG or WebP for free. PNG 16, 32, 180, 192, 512 px, site.webmanifest, ZIP and local browser processing.',
    },
    heicToJpg: {
      title: 'HEIC to JPG online, PNG and WebP converter - imgify.ru',
      description:
        'Convert HEIC and HEIF to JPG online for free, or choose PNG and WebP. Up to 10 files, local processing, quality controls and ZIP download.',
    },
    heicViewer: {
      title: 'Open HEIC online: view the file for free - imgify.ru',
      description:
        'View HEIC and HEIF photos online without server upload. Browser preview, local processing and optional JPG/PNG download.',
    },
    jpgViewer: {
      title: 'Open JPG online for free - imgify.ru',
      description:
        'Open JPG and JPEG online without server upload. Preview, zoom, file size, dimensions, MIME type and quick image actions.',
    },
    avifViewer: { title: 'Open AVIF online for free - imgify.ru', description: 'Open AVIF online without server upload, preview it and save JPG or PNG copies.' },
    pngViewer: {
      title: 'Open PNG online with transparency preview - imgify.ru',
      description:
        'Open PNG online without server upload. Inspect transparency on a checkerboard, zoom the preview, check file size and dimensions, then continue editing.',
    },
    webpViewer: {
      title: 'Open WebP online and view the file for free - imgify.ru',
      description:
        'Open WebP online without server upload. Local preview, zoom, file size and dimensions, plus optional JPG or PNG export.',
    },
    svgViewer: {
      title: 'Open SVG online with a safe preview - imgify.ru',
      description:
        'Open SVG online without server upload. Sanitized safe preview, file name, size, dimensions and optional PNG or JPG export.',
    },
    imageColorPicker: {
      title: 'Pick a color from an image online - imgify.ru',
      description:
        'Pick a pixel color from JPG, PNG or WebP online. Loupe, zoom, HEX, RGB, HSL, HSV and alpha with no image upload.',
    },
    tiffViewer: {
      title: 'Open TIFF or TIF online for free - imgify.ru',
      description: 'Open TIFF and TIF online without server upload. Local first-frame preview, page count and optional JPG export.',
    },
    gifViewer: {
      title: 'Open GIF online: animation viewer - imgify.ru',
      description:
        'Open GIF online without server upload. Animation in the browser, file size, dimensions and saving a frame as PNG or JPG.',
    },
    bmpViewer: {
      title: 'Open BMP online for free - imgify.ru',
      description:
        'Open BMP online without server upload. Preview, file size, dimensions and saving as PNG or JPG.',
    },
    icoViewer: {
      title: 'Open ICO online: icon viewer - imgify.ru',
      description:
        'Open ICO online without server upload. Icon preview, sizes stored in the file and saving as PNG or JPG.',
    },
    blog: {
      title: 'imgify blog - notes about images and formats',
      description:
        'Practical articles about PNG, JPG, image compression, local file processing and preparing pictures for websites.',
    },
  },
}
